'use client';

import { c as _c } from "react/compiler-runtime";
import { type ActivityLocationFilterType } from '@funkey/constants';
import type { I18n } from '@funkey/locales';
import { createContext, Fragment, useContext, useEffect, useLayoutEffect, useRef, useMemo, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useQueryState, parseAsArrayOf, parseAsString } from 'nuqs';
import { Button, ComboBox, Icon, Checkbox, Drawer, InputSelect, NumberInput, Spinner, Toggle, Tooltip, type IconName } from '~/components/primitives';
import { LAST_LOCATION_MODE, LAST_LOCATION_STORAGE } from '~/features/activities/lastLocation';
import { ChevronsDownUp, Copy, Search, X, Undo2, Sparkles } from 'lucide-react';
import { cx } from 'class-variance-authority';
import { std } from '@funkey/shared/utils';
import Title from '~/components/utils/Title';
import { useBrowseCategories, useCategoriesQuery, useLanguagesQuery } from '~/features/activities/ActivityFilterBar/config/shared';
import { RADIUS_OPTIONS } from '~/features/activities/ActivityFilterBar/config/region.activityFilters';
import { useI18n } from '~/features/i18n';
import { Switch } from '~/components/ui/switch';
import { useVATInclPreference } from '~/features/portals/money';
import { usePlpExperiment } from '../plpExperiment';
import { ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS } from '../shared';
import { useIsTailwindBreakpoint } from '~/modules/tailwind/hooks/useIsTailwindBreakpoint';
import { usePostHog } from 'posthog-js/react';
import { PostHog_ClientFeaturesSDK } from '~/modules/posthog/features/client';
import { GoogleMapsAutocomplete } from '~/features/google/maps';
import { usePortalCtx } from '~/features/global/portal';
import { useActivitySearchCtx } from './context';
import { useActivityFilterDrawerStore } from '../activity-filter-drawer-store';
import { FilterCheckboxLabel, FilterDrawerCheckboxList, FilterDrawerFieldLabel } from './filter-drawer-ui';
import { ACTIVITY_FILTER_SECTION_IDS, getActiveFilterCount, getBudgetVATBadge, isBudgetVATClearable } from './sidebar-utils';
import { useStickySidebarHeight } from './useStickySidebarHeight';
import { useActivitySearchHeroBarInViewStore } from '../activity-search-hero-bar-in-view-store';
import { ActivitySearchModeTabBar } from '../ActivitySearchModeTabBar';
import { ActivityAiChatPanel } from './ActivityAiChatPanel';
import { ActivityAiChatHeader } from './ActivityAiChatHeader';
import { ActivityAiHistoryHeader } from './ActivityAiHistoryDrawer';
import { useActivitySearchMode } from '../useActivitySearchMode';
import { DISMISSED_SEARCH_TURN } from '../activity-ai-search-turn';
import { useResultMessageIdParam } from './ActivityAiResultsBridge';
import { useActivityAiActiveChat } from '../useActivityAiActiveChat';
import { NEAR_POINT_CLEARED, NEAR_POINT_PICKED, isNearPointActive, useHasPrefilledNearPoint } from './nearPoint';
import { shouldClearAiActivityIdsOnDrawerConfirm, type AiFilterDrawerSnapshot } from './ai-filter-drawer-confirm';

// import type { ActivityAccessibilityFeature } from '@funkey/constants';

const SidebarSearchCtx = createContext('');
const useCollapsedFilters = () => {
  const $ = _c(25);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsArrayOf(parseAsString).withDefault([]).withOptions({
      shallow: true,
      history: "replace",
      scroll: false
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [collapsed, setCollapsed] = useQueryState("cf", t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = parseAsArrayOf(parseAsString).withDefault([]).withOptions({
      shallow: true,
      history: "replace",
      scroll: false
    });
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [opened, setOpened] = useQueryState("co", t1);
  let t2;
  if ($[2] !== setCollapsed || $[3] !== setOpened) {
    t2 = (id, defaultClosed) => {
      if (defaultClosed) {
        setOpened(prev => {
          const isClosing = prev.includes(id);
          if (isClosing) {
            const next = prev.filter(x => x !== id);
            return next.length === 0 ? null : next;
          }
          return [id];
        });
        setCollapsed(_temp);
      } else {
        setCollapsed(prev_1 => {
          const isClosing_0 = !prev_1.includes(id);
          if (isClosing_0) {
            const next_1 = [...prev_1, id];
            return next_1.length === 0 ? null : next_1;
          }
          const next_2 = [...ACTIVITY_FILTER_SECTION_IDS].filter(x_0 => x_0 !== id);
          return next_2.length === 0 ? null : next_2;
        });
        setOpened(null);
      }
    };
    $[2] = setCollapsed;
    $[3] = setOpened;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const toggle = t2;
  let t3;
  if ($[5] !== collapsed || $[6] !== opened) {
    t3 = (id_0, defaultClosed_0) => defaultClosed_0 ? opened.includes(id_0) : !collapsed.includes(id_0);
    $[5] = collapsed;
    $[6] = opened;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const isOpen = t3;
  let t4;
  if ($[8] !== setCollapsed || $[9] !== setOpened) {
    t4 = () => {
      setOpened(null);
      setCollapsed([...ACTIVITY_FILTER_SECTION_IDS]);
    };
    $[8] = setCollapsed;
    $[9] = setOpened;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const collapseAllSections = t4;
  let t5;
  if ($[11] !== setCollapsed || $[12] !== setOpened) {
    t5 = (id_1, defaultClosed_1) => {
      if (defaultClosed_1) {
        setOpened(prev_2 => {
          const next_3 = prev_2.filter(x_1 => x_1 !== id_1);
          return next_3.length === 0 ? null : next_3;
        });
      } else {
        setCollapsed(prev_3 => prev_3.includes(id_1) ? prev_3 : [...prev_3, id_1]);
      }
    };
    $[11] = setCollapsed;
    $[12] = setOpened;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const closeSection = t5;
  let t6;
  if ($[14] !== setCollapsed || $[15] !== setOpened) {
    t6 = (id_2, defaultClosed_2) => {
      if (defaultClosed_2) {
        setOpened(prev_4 => prev_4.includes(id_2) ? prev_4 : [...prev_4, id_2]);
      } else {
        setCollapsed(prev_5 => prev_5.filter(x_2 => x_2 !== id_2));
      }
    };
    $[14] = setCollapsed;
    $[15] = setOpened;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const openSection = t6;
  let t7;
  if ($[17] !== closeSection || $[18] !== collapseAllSections || $[19] !== collapsed || $[20] !== isOpen || $[21] !== openSection || $[22] !== opened || $[23] !== toggle) {
    t7 = {
      toggle,
      isOpen,
      collapseAllSections,
      closeSection,
      openSection,
      collapsed,
      opened
    };
    $[17] = closeSection;
    $[18] = collapseAllSections;
    $[19] = collapsed;
    $[20] = isOpen;
    $[21] = openSection;
    $[22] = opened;
    $[23] = toggle;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
};
const FilterCopyPageLinkButton: React.FC<{
  i18n: ReturnType<typeof useI18n>;
  className?: string;
}> = props => {
  const $ = _c(10);
  const [copied, setCopied] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async () => {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {}
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const onCopy = t0;
  let t1;
  if ($[1] !== props.className) {
    t1 = cx("flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-blue-500 transition hover:bg-white hover:text-blue-700", props.className);
    $[1] = props.className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Copy className="size-3.5 shrink-0" aria-hidden={true} />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== copied || $[5] !== props.i18n) {
    t3 = copied ? props.i18n._("activity.overview.filter.link_copied", undefined, {
      message: "Copied!"
    }) : props.i18n._("activity.overview.filter.copy_link", undefined, {
      message: "Copy link"
    });
    $[4] = copied;
    $[5] = props.i18n;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <button type="button" className={t1} onClick={onCopy}>{t2}{t3}</button>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const DEFAULT_CLOSED_SECTION_IDS = ACTIVITY_FILTER_SECTION_IDS;
const CollapseFilterSectionsIconButton: React.FC<{
  i18n: ReturnType<typeof useI18n>;
  /** Matches `ActivityFiltersContent` `excludeTopBarFilters` (sidebar vs drawer). */
  excludeTopBarFilters: boolean;
  className?: string;
}> = props => {
  const $ = _c(16);
  const ctx = useActivitySearchCtx();
  const {
    collapseAllSections,
    collapsed,
    opened
  } = useCollapsedFilters();
  let t0;
  bb0: {
    const skip = new Set();
    if (ctx.hiddenFilters.includes("budget")) {
      skip.add("budget");
    }
    if (ctx.hiddenFilters.includes("region")) {
      skip.add("regions");
    }
    for (const id of ACTIVITY_FILTER_SECTION_IDS) {
      if (skip.has(id)) {
        continue;
      }
      const defaultClosed = props.excludeTopBarFilters && (DEFAULT_CLOSED_SECTION_IDS as readonly string[]).includes(id);
      const isOpen = defaultClosed ? opened.includes(id) : !collapsed.includes(id);
      if (isOpen) {
        t0 = true;
        break bb0;
      }
    }
    t0 = false;
  }
  const hasAnyOpen = t0;
  let t1;
  if ($[0] !== props.i18n) {
    t1 = props.i18n._("activity.overview.filter.collapse_sections", undefined, {
      message: "Close all sections"
    });
    $[0] = props.i18n;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  let t2;
  if ($[2] !== props.i18n) {
    t2 = props.i18n._("activity.overview.filter.collapse_sections_tooltip", undefined, {
      message: "Collapse every filter section. Your selections stay applied."
    });
    $[2] = props.i18n;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const tooltip = t2;
  if (!hasAnyOpen) {
    return null;
  }
  let t3;
  if ($[4] !== props.className) {
    t3 = cx("flex size-7 shrink-0 items-center justify-center rounded-full text-blue-400 transition hover:bg-white hover:text-tertiary", props.className);
    $[4] = props.className;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== label) {
    t4 = <span className="sr-only">{label}</span>;
    $[6] = label;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronsDownUp className="size-3.5" aria-hidden={true} strokeWidth={2} />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== collapseAllSections || $[10] !== t3 || $[11] !== t4) {
    t6 = <button type="button" className={t3} onClick={collapseAllSections}>{t4}{t5}</button>;
    $[9] = collapseAllSections;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6 || $[14] !== tooltip) {
    t7 = <Tooltip content={tooltip} position="top">{t6}</Tooltip>;
    $[13] = t6;
    $[14] = tooltip;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
export const ActivityFilterDrawerButton: React.FC<{
  i18n: I18n;
  hideDesktopButton?: boolean;
}> = props => {
  const ctx = useActivitySearchCtx();
  const filters = ctx.useFilters();
  const activeFilterCount = useMemo(() => getActiveFilterCount(filters.value), [filters.value]);
  const filterCountElement = activeFilterCount ? <span className="absolute -right-1 -top-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-tertiary text-[10px] font-bold text-white sm:h-6 sm:w-6 sm:text-sm">
            <span className="mt-[2px] px-[2px] leading-none sm:mt-px">{activeFilterCount}</span>
        </span> : null;
  const openDrawer = useActivityFilterDrawerStore(s => s.open);
  return <>
            <button type="button" data-testid="filter-drawer-mobile-button" onClick={() => openDrawer()} className="relative block sm:hidden">
                <span className="sr-only">{props.i18n._('activity.overview.filter.cta')}</span>
                <Icon name="filters" />
                {filterCountElement}
            </button>

            <Button data-testid="filter-drawer-desktop-button" className={cx('relative hidden sm:flex', props.hideDesktopButton && 'lg:hidden')} variant="secondary" size="lg" iconBefore="filters" onClick={() => openDrawer()}>
                {props.i18n._('activity.overview.filter.cta')}
                {filterCountElement}
            </Button>
        </>;
};

/**
 * Store-driven filter drawer overlay. Mounted once near the page root so any trigger
 * (header button, AI composer "edit filters" button) can open it via the shared store —
 * including in AI mode, where the header filter button is not rendered.
 */
export const ActivityFilterDrawerOverlay: React.FC = () => {
  const i18n = useI18n();
  const ctx = useActivitySearchCtx();
  const filters = ctx.useFilters();
  const activeFilterCount = useMemo(() => getActiveFilterCount(filters.value), [filters.value]);
  const isOpen = useActivityFilterDrawerStore(s => s.isOpen);
  const source = useActivityFilterDrawerStore(s_0 => s_0.source);
  const section = useActivityFilterDrawerStore(s_1 => s_1.section);
  const closeDrawer = useActivityFilterDrawerStore(s_2 => s_2.close);
  const totalResults = useActivityFilterDrawerStore(s_3 => s_3.totalResults);
  const {
    collapseAllSections,
    openSection
  } = useCollapsedFilters();
  const [, setResultMessageId] = useResultMessageIdParam();
  const aiDrawerInitialFiltersRef = useRef<AiFilterDrawerSnapshot | null>(null);
  // The reworked page opens the filters as a sheet from the bottom on phones.
  // Both hooks run on every render: the flag can flip once PostHog loads, and
  // a hook behind `&&` would then change the hook order mid-life.
  const isFinetuning = usePlpExperiment();
  const isDesktop = useIsTailwindBreakpoint('lg');
  const isBottomSheet = isFinetuning && !isDesktop;
  // On phones the sheet is the whole filter, search by name included.
  const queryFilter = filters.getFilter('query');
  useEffect(() => {
    if (isOpen && source === 'ai' && aiDrawerInitialFiltersRef.current === null) {
      aiDrawerInitialFiltersRef.current = {
        ...filters.value
      } as AiFilterDrawerSnapshot;
    }
    if (!isOpen) aiDrawerInitialFiltersRef.current = null;
  }, [isOpen, source, filters.value]);

  // AI mode "Change search": the drawer fields already wrote the filters to the URL, so the
  // grid has them. Dismiss the derived pgvector hit set only when the confirmed drawer state
  // is a real net change from when it was opened. If the user temporarily edits a filter and
  // removes it back, keep the prior AI ids so the grid stays on the previous vector results
  // instead of falling back to the original Elastic results for an unchanged filter URL.
  const handleAddToSearch = useCallback(() => {
    if (shouldClearAiActivityIdsOnDrawerConfirm(aiDrawerInitialFiltersRef.current, filters.value as AiFilterDrawerSnapshot)) {
      void setResultMessageId(DISMISSED_SEARCH_TURN);
    }
    aiDrawerInitialFiltersRef.current = null;
    closeDrawer();
  }, [closeDrawer, filters.value, setResultMessageId]);

  // When opened targeting a section (e.g. clicking a result filter chip), focus that ONE
  // section: collapse everything else, open only the target (both modes cover default-open
  // and default-closed), then scroll it into view in the drawer and focus its first
  // text/number input. Opening via the plain "Edit filters" button (no section) leaves the
  // default layout untouched.
  useEffect(() => {
    if (!isOpen || !section) return;
    collapseAllSections();
    openSection(section, true);
    openSection(section, false);
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="filter-section-${section}"]`);
      if (!el) return;
      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth'
      });
      const input = el.querySelector('input:not([type="hidden"]):not([type="checkbox"])');
      if (input instanceof HTMLElement) input.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen, section, openSection, collapseAllSections]);
  const filterProps: FilterProps = {
    i18n,
    state: filters
  };
  return <AnimatePresence>
            {isOpen && <Drawer onClose={closeDrawer} side={isBottomSheet ? 'bottom' : 'left'} className={cx('bg-white', isBottomSheet ? 'w-full' : 'rounded-tr-2xl sm:w-96 sm:rounded-tr-4xl')}>
                    <Drawer.Header onClose={isBottomSheet ? undefined : closeDrawer} className={cx(isBottomSheet && 'pt-4 sm:px-6 sm:pt-4')} title={<div className="flex items-center gap-2">
                                <span>{`${i18n._('Filters')}`}</span>
                                {isBottomSheet && !!activeFilterCount && <button type="button" className="hit-area-2 ml-auto text-sm font-medium text-tertiary" onClick={() => {
          collapseAllSections();
          filters.clear();
        }}>
                                        {i18n._('activity.plp.clearAll', undefined, {
            message: 'Clear all'
          })}
                                    </button>}
                                {!isBottomSheet && !!activeFilterCount && <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-tertiary px-1.5 text-xs font-bold text-white">
                                        {activeFilterCount}
                                    </span>}
                                <CollapseFilterSectionsIconButton i18n={i18n} excludeTopBarFilters={false} />
                            </div>} />
                    <Drawer.Content className="group min-h-0 w-auto overscroll-y-contain pb-10">
                        {isBottomSheet && <label className="mb-3 flex h-11 items-center gap-3 rounded-full bg-brown-100/50 px-4 focus-within:bg-brown-200/60">
                                <Search className="size-4 shrink-0 text-blue-400" strokeWidth={1.75} aria-hidden />
                                <input type="search" data-testid="plp-mobile-search" value={queryFilter.value ?? ''} onChange={e => queryFilter.setValue(e.target.value || null)} placeholder={i18n._('Search activities')} className="w-full min-w-0 border-0 bg-transparent p-0 text-base text-blue placeholder:text-blue-400 focus:outline-none focus:ring-0" />
                            </label>}
                        <ActivityFiltersContent filterProps={filterProps} />
                    </Drawer.Content>
                    <Drawer.Footer className="mt-5 flex shrink-0 flex-col gap-3 rounded-lg bg-white py-5 sm:pb-5">
                        {source === 'ai' ?
        // Opened from the AI chat: confirm the filter changes (re-runs the
        // grid search), plus copy-link and clear, mirroring the default
        // footer's secondary actions.
        <>
                                <FilterCopyPageLinkButton i18n={i18n} />
                                <Button className="p-0! self-start text-sm text-tertiary" variant="link" size="xl" onClick={() => {
            collapseAllSections();
            filters.clear();
          }}>
                                    {i18n._('plp.filter_bar.cta.clear')}
                                </Button>
                                <Button data-testid="filter-drawer-add-button" className="w-full text-sm" variant="tertiary" size="xl" onClick={handleAddToSearch}>
                                    {i18n._('search.ai.filterDrawer.changeCta', undefined, {
              message: 'Change search'
            })}
                                </Button>
                            </> : <>
                                <FilterCopyPageLinkButton i18n={i18n} className={cx(isBottomSheet && 'justify-center')} />
                                <div className="flex justify-between gap-3">
                                    {/* The sheet already offers "Clear all" in its header. */}
                                    {!isBottomSheet && <Button className="p-0! shrink-0 text-sm text-tertiary" variant="link" size="xl" onClick={() => {
              collapseAllSections();
              filters.clear();
              closeDrawer();
            }}>
                                            {i18n._('plp.filter_bar.cta.clear')}
                                        </Button>}

                                    <Button data-testid="filter-drawer-apply-button" className={cx('text-sm', isBottomSheet && 'w-full')} variant="tertiary" size="xl" onClick={closeDrawer}>
                                        {isBottomSheet && totalResults !== null ? i18n._('activity.plp.drawer.showResults', {
                count: totalResults
              }) : i18n._('plp.filter_bar.cta.accept')}
                                    </Button>
                                </div>
                            </>}
                    </Drawer.Footer>
                </Drawer>}
        </AnimatePresence>;
};

/** Portal-agnostic filter rail body: search input + filter-mode toggle +
 * scrollable filter list. Reads everything from `useActivitySearchCtx`. No
 * sticky/aside chrome — caller wraps in whatever shell makes sense (WEB
 * sidebar, BIZZ edit-mode tab pane, etc.). */
export const ActivityFiltersRail: React.FC<{
  className?: string;
  showCopyLink?: boolean;
}> = props => {
  const isFinetuning = usePlpExperiment();
  const i18n = useI18n();
  const ctx = useActivitySearchCtx();
  const filters = ctx.useFilters();
  const [sidebarSearch, setSidebarSearch] = useQueryState('fq', parseAsString.withDefault('').withOptions({
    shallow: true,
    history: 'replace',
    scroll: false
  }));
  const searchInputRef = useRef<HTMLInputElement>(null);
  const queryFilter = filters.getFilter('query');
  const [isFilterModeChosen, setIsFilterMode] = useState(() => sidebarSearch.length > 0);
  // The reworked page hides the mode toggle, so an `fq` already in the URL
  // would strand this input in filter mode with no way back, while the AI
  // button — which reads the activity-search value — submitted nothing.
  const isFilterMode = isFinetuning ? false : isFilterModeChosen;
  const askAi = useAskAi(queryFilter);
  const searchValue = isFilterMode ? sidebarSearch : queryFilter.value ?? '';
  const setSearchValue = useCallback((val: string) => {
    if (isFilterMode) {
      setSidebarSearch(val || null);
    } else {
      queryFilter.setValue(val || null);
    }
  }, [isFilterMode, setSidebarSearch, queryFilter]);
  const {
    collapseAllSections
  } = useCollapsedFilters();
  const toggleFilterMode = useCallback(() => {
    setIsFilterMode(prev => {
      const next = !prev;
      if (next) {
        // Switching to filter mode — clear activity search, keep input focused
        queryFilter.setValue(null);
      } else {
        // Switching to activity mode — clear filter search
        setSidebarSearch(null);
      }
      searchInputRef.current?.focus();
      return next;
    });
    // Collapse outside the updater to avoid setState-during-render warning
    if (isFilterMode) {
      collapseAllSections();
    }
  }, [queryFilter, setSidebarSearch, collapseAllSections, isFilterMode]);
  const sidebarSearchFiltersToggleLabel = i18n._('activity.overview.filter.search_filters_tooltip', undefined, {
    message: 'Search filters'
  });
  return <div className={cx('flex min-h-0 flex-1 flex-col gap-0.5 overflow-hidden', props.className)}>
            <div className={cx('group/search relative mb-2 flex h-9 shrink-0 items-center rounded-full border border-transparent bg-brown-100/50 px-1 transition-colors focus-within:border-brown-200/40 focus-within:bg-brown-200/60 hover:bg-brown-200/50', isFinetuning && 'mt-3')}>
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-blue-400" aria-hidden />
                <input ref={searchInputRef} type="text" data-testid="sidebar-search-input" value={searchValue} onChange={e => setSearchValue(e.target.value)} placeholder={isFilterMode ? i18n._('activity.overview.filter.search_placeholder', undefined, {
        message: 'Search filters..'
      }) : i18n._('Search activities')} onKeyDown={event => askAi.onKeyDown(event, isFinetuning)} className="font-regular pr-17 h-9 w-full min-w-0 translate-y-px border-0 bg-transparent py-0 pl-7 text-sm leading-9 text-blue ring-0 placeholder:text-blue-400 focus:border-0 focus:outline-none focus:ring-0" />
                <div className="absolute right-1.5 flex items-center gap-px">
                    {isFinetuning && searchValue.trim().length > 0 && <button type="button" data-testid="sidebar-ask-ai" aria-label={i18n._('activity.plp.aiSearch.cta', undefined, {
          message: 'Ask the AI'
        })} className="rounded-full bg-secondary p-1.5 text-white transition-colors hover:bg-secondary-700" onClick={askAi.ask}>
                            <Sparkles className="size-3.5" aria-hidden />
                        </button>}
                    {!isFinetuning && <Tooltip content={sidebarSearchFiltersToggleLabel} position="top">
                            <button type="button" data-testid="sidebar-search-filter-toggle" aria-pressed={isFilterMode} aria-label={sidebarSearchFiltersToggleLabel} className={cx('rounded-full p-1.5 text-brown-700 transition-all hover:bg-brown-300/70 hover:text-brown-700', isFilterMode ? 'bg-brown-300 text-brown-700 hover:bg-brown-200' : 'opacity-0 group-focus-within/search:opacity-100 group-hover/search:opacity-100')} onClick={toggleFilterMode}>
                                <Icon name="filters" size="xs" />
                            </button>
                        </Tooltip>}
                    {searchValue.length > 0 ? <button type="button" data-testid="sidebar-search-clear" className="rounded-full p-1.5 text-blue-400 transition-colors hover:bg-brown-300/70 hover:text-blue-600" onClick={() => {
          setSearchValue('');
          searchInputRef.current?.focus();
        }} aria-label={i18n._('activity.search.clear', undefined, {
          message: 'Clear search'
        })}>
                            <X className="size-3.5 shrink-0" aria-hidden />
                        </button> : null}
                </div>
            </div>

            <div className="-mx-2 flex-1 overflow-y-auto overscroll-y-contain rounded-xl px-2 transition-all [scrollbar-gutter:stable]">
                <SidebarSearchCtx.Provider value={sidebarSearch}>
                    <div className="flex flex-col gap-1">
                        <ActivityFiltersContent filterProps={{
            i18n,
            state: filters
          }} showBottomActions />
                    </div>
                </SidebarSearchCtx.Provider>
            </div>
        </div>;
};
export const ActivityFiltersSidebar: React.FC<{
  className?: string;
  totalResults?: number;
  isFetching?: boolean;
  dark?: boolean;
}> = props => {
  const i18n = useI18n();
  const ctx = useActivitySearchCtx();
  const filters = ctx.useFilters();
  // The reworked page states the count above the results, once.
  const isFinetuning = usePlpExperiment();
  const sidebarRef = useStickySidebarHeight<HTMLElement>();
  const [sidebarSearch, setSidebarSearch] = useQueryState('fq', parseAsString.withDefault('').withOptions({
    shallow: true,
    history: 'replace',
    scroll: false
  }));
  const searchInputRef = useRef<HTMLInputElement>(null);
  const queryFilter = filters.getFilter('query');
  const [isFilterModeChosen, setIsFilterMode] = useState(() => sidebarSearch.length > 0);
  // The reworked page hides the mode toggle, so an `fq` already in the URL
  // would strand this input in filter mode with no way back, while the AI
  // button — which reads the activity-search value — submitted nothing.
  const isFilterMode = isFinetuning ? false : isFilterModeChosen;
  const askAi = useAskAi(queryFilter);
  const searchValue = isFilterMode ? sidebarSearch : queryFilter.value ?? '';
  const setSearchValue = useCallback((val: string) => {
    if (isFilterMode) {
      setSidebarSearch(val || null);
    } else {
      queryFilter.setValue(val || null);
    }
  }, [isFilterMode, setSidebarSearch, queryFilter]);
  const {
    collapseAllSections
  } = useCollapsedFilters();
  const heroBarInView = useActivitySearchHeroBarInViewStore(s => s.heroBarInView);
  const {
    isAi,
    setMode
  } = useActivitySearchMode();
  // Filters mode keeps the toggle in the hero pill, so the sidebar only needs the tabs once
  // that pill scrolls away. AI mode hides the composer bar (its toggle) as soon as results
  // show, so the sidebar is the only persistent home for the toggle — always show it there.
  const showFloatingModeTabs = isAi || !heroBarInView;
  // The AI sidebar is always mounted in AI mode; its header "close" exits back to Filters
  // rather than hiding a column that no longer collapses (ADR 0006).
  const closeAiSidebar = () => setMode('normal');
  const aiChat = useActivityAiActiveChat();
  const [aiHistoryOpen, setAiHistoryOpen] = useState(false);
  const shouldUseRaisedShell = props.dark || isAi;
  const toggleFilterMode = useCallback(() => {
    setIsFilterMode(prev => {
      const next = !prev;
      if (next) {
        // Switching to filter mode — clear activity search, keep input focused
        queryFilter.setValue(null);
      } else {
        // Switching to activity mode — clear filter search
        setSidebarSearch(null);
      }
      searchInputRef.current?.focus();
      return next;
    });
    // Collapse outside the updater to avoid setState-during-render warning
    if (isFilterMode) {
      collapseAllSections();
    }
  }, [queryFilter, setSidebarSearch, collapseAllSections, isFilterMode]);
  const filterProps: FilterProps = {
    i18n,
    state: filters
  };
  const sidebarSearchFiltersToggleLabel = i18n._('activity.overview.filter.search_filters_tooltip', undefined, {
    message: 'Search filters'
  });
  return <aside ref={sidebarRef} data-testid="activity-filter-sidebar" className={cx('hidden min-h-0 overflow-visible lg:block', props.className)}>
            <div className={cx('rounded-[28px]', 'h-full', shouldUseRaisedShell && 'shadow-lg')}>
                <div className={cx('flex h-full min-h-0 flex-col overflow-hidden rounded-[28px]', shouldUseRaisedShell ? 'bg-white' : 'bg-white/0')}>
                    <AnimatePresence initial={false}>
                        {showFloatingModeTabs ? <motion.div key="filter-sidebar-mode-tabs-duplicate" initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} className="shrink-0 overflow-hidden">
                                <div className="px-6 pb-4 pt-2">
                                    <ActivitySearchModeTabBar variant="sidebar" />
                                </div>
                            </motion.div> : null}
                    </AnimatePresence>
                    {isAi ? <>
                            {aiHistoryOpen ? <ActivityAiHistoryHeader onClose={() => setAiHistoryOpen(false)} onCloseSheet={closeAiSidebar} className={cx(!showFloatingModeTabs && shouldUseRaisedShell && 'pt-6')} /> : <ActivityAiChatHeader hasActiveThread={aiChat.chatId.length > 0} threadTitle={aiChat.threadTitle} onBack={aiChat.goToAllChats} onClose={closeAiSidebar} className={cx(!showFloatingModeTabs && shouldUseRaisedShell && 'pt-6')} />}
                            <ActivityAiChatPanel historyOpen={aiHistoryOpen} historyHeaderInParent onOpenHistory={() => setAiHistoryOpen(true)} onCloseHistory={() => setAiHistoryOpen(false)} onCloseDrawer={closeAiSidebar} />
                        </> : <>
                            <div className={cx('flex items-center justify-between gap-3 px-6', !showFloatingModeTabs && shouldUseRaisedShell && 'pt-6')}>
                                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                                    <Title size="lg" className="translate-y-px text-base leading-tight">
                                        {i18n._('Filters')}
                                    </Title>
                                    {/* {!!activeFilterCount && (
                                     <Badge variant="passive">{activeFilterCount}</Badge>
                                     )} */}
                                    {/* <CollapseFilterSectionsIconButton i18n={i18n} excludeTopBarFilters /> */}
                                </div>

                                {/* <button
                                 type="button"
                                 data-testid="sidebar-reset-button"
                                 className={cx(
                                 'shrink-0 rounded-full px-3 py-1.5 text-sm font-medium text-tertiary transition',
                                 activeFilterCount ? 'hover:bg-white' : 'pointer-events-none invisible',
                                 )}
                                 onClick={() => {
                                 collapseAllSections();
                                 filters.clear();
                                 }}
                                 >
                                 {i18n._('Reset')}
                                 </button> */}
                            </div>
                            {!isFinetuning && (props.isFetching || props.totalResults !== undefined) && <div className="flex min-h-8 w-full min-w-0 shrink-0 items-center px-6 pb-2">
                                        <AnimatePresence mode="popLayout" initial={false} presenceAffectsLayout={false}>
                                            {props.isFetching ? <motion.span key="sidebar-total-loading" layout initial={{
                opacity: 0,
                scale: 0.94
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.94
              }} transition={{
                type: 'spring',
                stiffness: 420,
                damping: 32
              }} className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap text-sm font-medium text-blue-400">
                                                    <Spinner className="size-3.5! shrink-0" aria-hidden />
                                                    <span className="min-w-0 truncate">
                                                        {i18n._('activity.overview.filter.loading_teambuildings', undefined, {
                    message: 'Loading teambuildings..'
                  })}
                                                    </span>
                                                </motion.span> : <motion.span key={`sidebar-total-${props.totalResults}`} layout data-testid="sidebar-total-results" initial={{
                opacity: 0,
                scale: 0.94
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.94
              }} transition={{
                type: 'spring',
                stiffness: 420,
                damping: 32
              }} className="block max-w-full whitespace-nowrap text-sm font-medium text-blue-400">
                                                    {props.totalResults} {i18n._('teambuildings')}
                                                </motion.span>}
                                        </AnimatePresence>
                                    </div>}

                            <div className="group/search relative mx-3.5 mb-0.5 mt-3 flex h-9 items-center rounded-full border border-transparent bg-brown-100/50 px-1 transition-colors focus-within:border-brown-200/40 focus-within:bg-brown-200/60 hover:bg-brown-200/50">
                                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-blue-400" aria-hidden />
                                <input ref={searchInputRef} type="text" data-testid="sidebar-search-input" value={searchValue} onChange={e => setSearchValue(e.target.value)} placeholder={isFilterMode ? i18n._('activity.overview.filter.search_placeholder', undefined, {
              message: 'Search filters..'
            }) : i18n._('Search activities')} onKeyDown={event => askAi.onKeyDown(event, isFinetuning)} className="font-regular pr-17 h-9 w-full min-w-0 translate-y-px border-0 bg-transparent py-0 pl-7 text-sm leading-9 text-blue ring-0 placeholder:text-blue-400 focus:border-0 focus:outline-none focus:ring-0" />
                                <div className="absolute right-1.5 flex items-center gap-px">
                                    {isFinetuning && searchValue.trim().length > 0 && <button type="button" data-testid="sidebar-ask-ai" aria-label={i18n._('activity.plp.aiSearch.cta', undefined, {
                message: 'Ask the AI'
              })} className="rounded-full bg-secondary p-1.5 text-white transition-colors hover:bg-secondary-700" onClick={askAi.ask}>
                                            <Sparkles className="size-3.5" aria-hidden />
                                        </button>}
                                    {!isFinetuning && <Tooltip content={sidebarSearchFiltersToggleLabel} position="top">
                                            <button type="button" data-testid="sidebar-search-filter-toggle" aria-pressed={isFilterMode} aria-label={sidebarSearchFiltersToggleLabel} className={cx('rounded-full p-1.5 text-brown-700 transition-all hover:bg-brown-300/70 hover:text-brown-700', isFilterMode ? 'bg-brown-300 text-brown-700 hover:bg-brown-200' : 'opacity-0 group-focus-within/search:opacity-100 group-hover/search:opacity-100')} onClick={toggleFilterMode}>
                                                <Icon name="filters" size="xs" />
                                            </button>
                                        </Tooltip>}
                                    {searchValue.length > 0 ? <button type="button" data-testid="sidebar-search-clear" className="rounded-full p-1.5 text-blue-400 transition-colors hover:bg-brown-300/70 hover:text-blue-600" onClick={() => {
                setSearchValue('');
                searchInputRef.current?.focus();
              }} aria-label={i18n._('activity.search.clear', undefined, {
                message: 'Clear search'
              })}>
                                            <X className="size-3.5 shrink-0" aria-hidden />
                                        </button> : null}
                                </div>
                            </div>

                            <div className="min-h-0 flex-1 overflow-y-auto rounded-xl px-4 pb-4 transition-all [scrollbar-gutter:stable]">
                                <SidebarSearchCtx.Provider value={sidebarSearch}>
                                    <div className="flex flex-col gap-1">
                                        <ActivityFiltersContent filterProps={filterProps} excludeTopBarFilters showBottomActions />
                                    </div>
                                </SidebarSearchCtx.Provider>
                            </div>
                        </>}
                </div>
            </div>
        </aside>;
};
type FilterProps = {
  i18n: ReturnType<typeof useI18n>;
  state: useActivitySearchCtx.AllFiltersState;
};
const FilterTileGroup: React.FC<{
  columns?: 2 | 3;
  className?: string;
  children: React.ReactNode;
}> = props => {
  const $ = _c(6);
  const t0 = props.columns === 2 ? "grid-cols-2" : "grid-cols-3";
  let t1;
  if ($[0] !== props.className || $[1] !== t0) {
    t1 = cx("grid rounded-xl", t0, props.className);
    $[0] = props.className;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.children || $[4] !== t1) {
    t2 = <div className={t1}>{props.children}</div>;
    $[3] = props.children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const FilterTileButton: React.FC<{
  selected: boolean;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
  /** Grid position for computing border-radius on outer edges */
  index?: number;
  total?: number;
  columns?: 2 | 3;
  /** Render icon and label side-by-side instead of stacked */
  horizontal?: boolean;
}> = props => {
  const $ = _c(17);
  const cols = props.columns ?? 3;
  const index = props.index ?? 0;
  const total = props.total ?? 1;
  const col = index % cols;
  const row = Math.floor(index / cols);
  const lastRow = Math.floor((total - 1) / cols);
  const isFirstRow = row === 0;
  const isLastRow = row === lastRow;
  const isFirstCol = col === 0;
  const isLastCol = col === cols - 1 || index === total - 1;
  const t0 = props.horizontal ? "flex-row gap-1.5 px-2 py-1.5" : "flex-col gap-1.5 px-2 py-2";
  const t1 = props.selected ? "z-1 border-secondary bg-secondary-50" : "border-blue-200 hover:z-1 hover:border-blue-400 hover:bg-blue-50";
  const t2 = isFirstRow && isFirstCol && "rounded-tl-xl";
  const t3 = isFirstRow && isLastCol && "rounded-tr-xl";
  const t4 = isLastRow && isFirstCol && "rounded-bl-xl";
  const t5 = isLastRow && isLastCol && "rounded-br-xl";
  let t6;
  if ($[0] !== props.className || $[1] !== t0 || $[2] !== t1 || $[3] !== t2 || $[4] !== t3 || $[5] !== t4 || $[6] !== t5) {
    t6 = cx("relative z-0 -mb-px -mr-px flex items-center justify-center border bg-white text-center transition", t0, t1, t2, t3, t4, t5, props.className);
    $[0] = props.className;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== props.selected) {
    t7 = props.selected && <motion.div initial={{
      opacity: 0,
      scale: 0.5
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.5
    }} transition={{
      duration: 0.15
    }} className="absolute left-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-secondary"><Icon name="check" className="text-white" size="xs" /></motion.div>;
    $[8] = props.selected;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <AnimatePresence>{t7}</AnimatePresence>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== props.children || $[13] !== props.onClick || $[14] !== t6 || $[15] !== t8) {
    t9 = <button type="button" className={t6} onClick={props.onClick}>{t8}{props.children}</button>;
    $[12] = props.children;
    $[13] = props.onClick;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
};
const FilterLayout: React.FC<{
  id: string;
  title: React.ReactNode;
  i18n: I18n;
  icon?: IconName;
  onClear?: false | (() => void);
  /** Short summary shown next to the title when collapsed and a filter is active */
  badge?: React.ReactNode;
  /** How many values are active in this section; the reworked page shows the
   * number instead of the summary. */
  activeCount?: number;
  /** When true, this filter section starts collapsed */
  defaultClosed?: boolean;
  /** Focus the first text/number input when the user opens this section (not on initial mount). */
  focusContentOnOpen?: boolean;
  /** CSS selector for the element to focus when opened. Defaults to first visible input. */
  focusSelector?: string;
  /** Searchable keywords for sidebar search (joined with title for matching). */
  searchKeywords?: string[];
  children: React.ReactNode;
}> = props => {
  const $ = _c(105);
  const {
    toggle,
    isOpen: isOpenFn,
    closeSection,
    openSection
  } = useCollapsedFilters();
  const isFinetuning = usePlpExperiment();
  let t0;
  if ($[0] !== isOpenFn || $[1] !== props.defaultClosed || $[2] !== props.id) {
    t0 = isOpenFn(props.id, props.defaultClosed);
    $[0] = isOpenFn;
    $[1] = props.defaultClosed;
    $[2] = props.id;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const isOpen = t0;
  const contentInnerRef = useRef(null);
  const prevIsOpenRef = useRef(null);
  const searchTerm = useContext(SidebarSearchCtx);
  const isSearching = searchTerm.length > 0;
  let t1;
  bb0: {
    if (!isSearching) {
      t1 = true;
      break bb0;
    }
    let t2;
    if ($[4] !== props.searchKeywords || $[5] !== props.title || $[6] !== searchTerm) {
      const q = searchTerm.toLowerCase();
      const titleStr = typeof props.title === "string" ? props.title : "";
      let t3;
      if ($[8] !== props.searchKeywords) {
        t3 = props.searchKeywords ?? [];
        $[8] = props.searchKeywords;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      let t4;
      if ($[10] !== t3 || $[11] !== titleStr) {
        t4 = [titleStr, ...t3];
        $[10] = t3;
        $[11] = titleStr;
        $[12] = t4;
      } else {
        t4 = $[12];
      }
      const haystack = t4.join(" ").toLowerCase();
      t2 = haystack.includes(q);
      $[4] = props.searchKeywords;
      $[5] = props.title;
      $[6] = searchTerm;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t1 = t2;
  }
  const matchesSearch = t1;
  let t2;
  let t3;
  if ($[13] !== isOpen || $[14] !== isSearching || $[15] !== matchesSearch || $[16] !== openSection || $[17] !== props.defaultClosed || $[18] !== props.id) {
    t2 = () => {
      if (isSearching && matchesSearch && !isOpen) {
        openSection(props.id, props.defaultClosed);
      }
    };
    t3 = [isSearching, matchesSearch, isOpen, openSection, props.id, props.defaultClosed];
    $[13] = isOpen;
    $[14] = isSearching;
    $[15] = matchesSearch;
    $[16] = openSection;
    $[17] = props.defaultClosed;
    $[18] = props.id;
    $[19] = t2;
    $[20] = t3;
  } else {
    t2 = $[19];
    t3 = $[20];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[21] !== isOpen || $[22] !== isSearching || $[23] !== props.focusContentOnOpen || $[24] !== props.focusSelector) {
    t4 = () => {
      if (!props.focusContentOnOpen || isSearching) {
        return;
      }
      if (prevIsOpenRef.current === null) {
        prevIsOpenRef.current = isOpen;
        return;
      }
      const openedByUser = isOpen && !prevIsOpenRef.current;
      prevIsOpenRef.current = isOpen;
      if (!openedByUser) {
        return;
      }
      const id = requestAnimationFrame(() => {
        const selector = props.focusSelector ?? "input:not([type=\"hidden\"])";
        const input = contentInnerRef.current?.querySelector(selector);
        input?.focus();
      });
      return () => cancelAnimationFrame(id);
    };
    t5 = [isOpen, isSearching, props.focusContentOnOpen, props.focusSelector];
    $[21] = isOpen;
    $[22] = isSearching;
    $[23] = props.focusContentOnOpen;
    $[24] = props.focusSelector;
    $[25] = t4;
    $[26] = t5;
  } else {
    t4 = $[25];
    t5 = $[26];
  }
  useLayoutEffect(t4, t5);
  if (isSearching && !matchesSearch) {
    return null;
  }
  const badge = isFinetuning ? undefined : props.badge;
  const hasActiveFilter = !!props.badge || !!props.activeCount;
  const t6 = `filter-section-${props.id}`;
  const t7 = isFinetuning ? "-mx-3 px-3" : "-mx-2 px-2";
  const t8 = isOpen && "bg-white py-2";
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = cx("group/filter rounded-xl transition-all", t7, t8);
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const t10 = `filter-section-${props.id}-toggle`;
  const t11 = isFinetuning ? "py-2.5 text-base lg:py-2 lg:text-sm" : "py-1.5 text-[13px]";
  const t12 = isFinetuning || isOpen || hasActiveFilter ? "opacity-100" : "opacity-50 hover:opacity-100";
  let t13;
  if ($[30] !== t11 || $[31] !== t12) {
    t13 = cx("relative flex w-full select-none items-center justify-between px-2 transition", t11, t12);
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== props.defaultClosed || $[34] !== props.id || $[35] !== toggle) {
    t14 = () => toggle(props.id, props.defaultClosed);
    $[33] = props.defaultClosed;
    $[34] = props.id;
    $[35] = toggle;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = isFinetuning ? "gap-2.5" : "gap-1.5";
  let t16;
  if ($[37] !== t15) {
    t16 = cx("flex min-w-0 flex-1 items-center", t15);
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== props.icon) {
    t17 = props.icon && <Icon name={props.icon} size="xs" className="shrink-0 text-tertiary-400" />;
    $[39] = props.icon;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const t18 = isFinetuning ? "text-base lg:text-sm" : "text-[13px]";
  let t19;
  if ($[41] !== t18) {
    t19 = cx("truncate font-medium leading-tight", t18);
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== props.title || $[44] !== t19) {
    t20 = <span className={t19}>{props.title}</span>;
    $[43] = props.title;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== isFinetuning || $[47] !== isOpen || $[48] !== props.activeCount || $[49] !== props.id) {
    t21 = isFinetuning && !isOpen && !!props.activeCount && <span data-testid={`filter-section-${props.id}-active`} className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-secondary/15 px-1.5 text-[11px] font-bold leading-none text-secondary">{props.activeCount}</span>;
    $[46] = isFinetuning;
    $[47] = isOpen;
    $[48] = props.activeCount;
    $[49] = props.id;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== badge || $[52] !== isOpen) {
    t22 = !isOpen && badge && <motion.span className="flex items-center" initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.9
    }} transition={{
      duration: 0.15
    }}><Tooltip content={badge} position="bottom" enabled={typeof badge === "string" && badge.length > 20}><span className="inline-flex min-w-0 max-w-[200px] items-center truncate rounded-full bg-secondary-100 px-1.5 py-0.5 text-[11px] font-medium leading-normal text-secondary-700">{badge}</span></Tooltip></motion.span>;
    $[51] = badge;
    $[52] = isOpen;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t22) {
    t23 = <AnimatePresence>{t22}</AnimatePresence>;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t16 || $[57] !== t17 || $[58] !== t20 || $[59] !== t21 || $[60] !== t23) {
    t24 = <span className={t16}>{t17}{t20}{t21}{t23}</span>;
    $[56] = t16;
    $[57] = t17;
    $[58] = t20;
    $[59] = t21;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== closeSection || $[63] !== isSearching || $[64] !== props) {
    t25 = !isSearching && <Tooltip content={props.i18n._("clear selection")} position="bottom" enabled={!!props.onClear}><span className={cx("flex size-6 select-none items-center justify-center rounded-full text-tertiary-700 transition hover:bg-brown-200 lg:opacity-0 lg:group-hover/filter:opacity-100", !props.onClear && "pointer-events-none invisible")} role="button" tabIndex={0} onClick={e => {
        e.stopPropagation();
        if (props.onClear) {
          props.onClear();
          closeSection(props.id, props.defaultClosed);
        }
      }} onKeyDown={e_0 => {
        if (e_0.key === "Enter" || e_0.key === " ") {
          e_0.stopPropagation();
          if (props.onClear) {
            props.onClear();
            closeSection(props.id, props.defaultClosed);
          }
        }
      }}><span className="sr-only">{props.i18n._("clear selection")}</span><Undo2 className="h-3 w-3" /></span></Tooltip>;
    $[62] = closeSection;
    $[63] = isSearching;
    $[64] = props;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== closeSection || $[67] !== isSearching || $[68] !== props.defaultClosed || $[69] !== props.id) {
    t26 = isSearching && <span className="flex size-6 select-none items-center justify-center rounded-full text-blue-400 transition hover:bg-brown-200 hover:text-blue-600" role="button" tabIndex={0} onClick={e_1 => {
      e_1.stopPropagation();
      closeSection(props.id, props.defaultClosed);
    }} onKeyDown={e_2 => {
      if (e_2.key === "Enter" || e_2.key === " ") {
        e_2.stopPropagation();
        closeSection(props.id, props.defaultClosed);
      }
    }}><X className="size-3.5" aria-hidden={true} /></span>;
    $[66] = closeSection;
    $[67] = isSearching;
    $[68] = props.defaultClosed;
    $[69] = props.id;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t25 || $[72] !== t26) {
    t27 = <div className="flex shrink-0 items-center gap-1">{t25}{t26}</div>;
    $[71] = t25;
    $[72] = t26;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  const t28 = isFinetuning || !isSearching && (isOpen || hasActiveFilter) ? "w-3 opacity-100" : "w-0 opacity-0";
  let t29;
  if ($[74] !== t28) {
    t29 = cx("inline-flex shrink-0 items-center overflow-hidden transition-all duration-200", t28);
    $[74] = t28;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== isOpen || $[77] !== props.i18n) {
    t30 = isOpen ? props.i18n._("Close section") : props.i18n._("Open section");
    $[76] = isOpen;
    $[77] = props.i18n;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  const t31 = !isOpen && "-rotate-90";
  let t32;
  if ($[79] !== t31) {
    t32 = cx("text-blue-400 transition-transform duration-200", t31);
    $[79] = t31;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== t32) {
    t33 = <Icon name="chevron-down" size="xs" className={t32} />;
    $[81] = t32;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== t29 || $[84] !== t30 || $[85] !== t33) {
    t34 = <span className={t29} title={t30}>{t33}</span>;
    $[83] = t29;
    $[84] = t30;
    $[85] = t33;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  let t35;
  if ($[87] !== t10 || $[88] !== t13 || $[89] !== t14 || $[90] !== t24 || $[91] !== t27 || $[92] !== t34) {
    t35 = <button type="button" data-testid={t10} className={t13} onClick={t14}>{t24}{t27}{t34}</button>;
    $[87] = t10;
    $[88] = t13;
    $[89] = t14;
    $[90] = t24;
    $[91] = t27;
    $[92] = t34;
    $[93] = t35;
  } else {
    t35 = $[93];
  }
  let t36;
  if ($[94] !== isFinetuning || $[95] !== isOpen || $[96] !== props.children) {
    t36 = isOpen && <motion.div key="filter-section-body" initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: "easeOut"
    }} className="overflow-hidden"><div ref={contentInnerRef} className={cx("flex flex-col gap-px py-px", isFinetuning ? "px-1" : "px-2")}>{props.children}</div></motion.div>;
    $[94] = isFinetuning;
    $[95] = isOpen;
    $[96] = props.children;
    $[97] = t36;
  } else {
    t36 = $[97];
  }
  let t37;
  if ($[98] !== t36) {
    t37 = <AnimatePresence initial={false}>{t36}</AnimatePresence>;
    $[98] = t36;
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  let t38;
  if ($[100] !== t35 || $[101] !== t37 || $[102] !== t6 || $[103] !== t9) {
    t38 = <section data-testid={t6} className={t9}>{t35}{t37}</section>;
    $[100] = t35;
    $[101] = t37;
    $[102] = t6;
    $[103] = t9;
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  return t38;
};
const FilterSortSection: React.FC<{
  defaultClosed?: boolean;
}> = props => {
  const i18n = useI18n();
  const ctx = useActivitySearchCtx();
  const sort = ctx.useFilter('sort');
  const sortCenterLat = ctx.useFilter('sortCenterLat');
  const sortCenterLng = ctx.useFilter('sortCenterLng');
  const hasCenter = sortCenterLat.value != null && sortCenterLng.value != null;
  const options = useMemo(() => {
    const base: Array<{
      value: NonNullable<typeof sort.value>;
      label: string;
    }> = [{
      value: 'popularity.DESC',
      label: i18n._('Relevance')
    }, {
      value: 'price.ASC',
      label: i18n._('Price (Low to High)')
    }, {
      value: 'price.DESC',
      label: i18n._('Price (High to Low)')
    }, {
      value: 'createdAt.ASC',
      label: i18n._('Date (Oldest - Newest)')
    }, {
      value: 'createdAt.DESC',
      label: i18n._('Date (Newest - Oldest)')
    }];
    if (hasCenter) {
      base.push({
        value: 'distance.ASC',
        label: i18n._('Distance (closest first)')
      });
    }
    return base;
  }, [i18n.locale, hasCenter]);
  const currentLabel = options.find(o => o.value === sort.value)?.label ?? i18n._('Relevance');
  return <FilterLayout id="sort" i18n={i18n} icon="sort" title={i18n._('Sort by')} defaultClosed={props.defaultClosed} badge={currentLabel} activeCount={sort.value !== 'popularity.DESC' ? 1 : 0} onClear={sort.value !== 'popularity.DESC' ? () => sort.setValue('popularity.DESC') : false}>
            <InputSelect value={sort.value} placeholder={i18n._('Relevance')} options={options} onChange={newSort => sort.setValue(newSort as typeof sort.value)} className="h-auto w-full" compact />
        </FilterLayout>;
};
const FilterBudgetAndVAT: React.FC<FilterProps & {
  defaultClosed?: boolean;
  /** Budget already sits in the filter bar: keep only the VAT toggle,
   * which has no other home. On Bizz VAT is forced, so nothing is left. */
  hideBudget?: boolean;
}> = props => {
  const $ = _c(59);
  const minBudget = props.state.getFilter("minBudget");
  const maxBudget = props.state.getFilter("maxBudget");
  const vatPref = useVATInclPreference();
  const portal = usePortalCtx();
  const isBizz = portal.portal === "BIZZ";
  const bizzVATInclusive = isBizz && portal.bizz.vatInclusive === true;
  if (props.hideBudget) {
    return null;
  }
  const vatCtx = {
    isBizz,
    bizzVATInclusive,
    bizzVATRate: isBizz && bizzVATInclusive ? portal.bizz.vatRate : null,
    minBudget: minBudget.value,
    maxBudget: maxBudget.value,
    vatPrefEnabled: vatPref.enabled,
    vatPrefRate: vatPref.rate
  };
  const badge = getBudgetVATBadge(vatCtx);
  const clearable = isBudgetVATClearable(vatCtx);
  const t0 = props.i18n;
  let t1;
  if ($[0] !== props.hideBudget || $[1] !== props.i18n) {
    t1 = props.hideBudget ? props.i18n._("VAT") : props.i18n._("Budget");
    $[0] = props.hideBudget;
    $[1] = props.i18n;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !props.hideBudget;
  const t3 = props.defaultClosed;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["VAT", "BTW", "tax", "incl", "excl", "budget"];
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = props.hideBudget ? 0 : clearable ? 1 : 0;
  let t6;
  if ($[4] !== clearable || $[5] !== isBizz || $[6] !== props.state || $[7] !== vatPref) {
    t6 = clearable && (() => {
      props.state.update({
        minBudget: null,
        maxBudget: null
      });
      if (!isBizz) {
        vatPref.setEnabled(false);
      }
    });
    $[4] = clearable;
    $[5] = isBizz;
    $[6] = props.state;
    $[7] = vatPref;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = !isBizz && "mb-3";
  const t8 = props.hideBudget && "hidden";
  let t9;
  if ($[9] !== t7 || $[10] !== t8) {
    t9 = cx(t7, t8);
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== props.i18n) {
    t10 = props.i18n._("Budget");
    $[12] = props.i18n;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10) {
    t11 = <FilterDrawerFieldLabel>{t10}</FilterDrawerFieldLabel>;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== props.i18n) {
    t12 = props.i18n._("Min. Budget");
    $[16] = props.i18n;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = minBudget.value || undefined;
  let t14;
  if ($[18] !== minBudget) {
    t14 = newMin => minBudget.setValue(newMin || null);
    $[18] = minBudget;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t12 || $[21] !== t13 || $[22] !== t14) {
    t15 = <NumberInput title={t12} placeholder="0" className="mb-0!" min={0} value={t13} onChange={t14} step={1} hideButtons={true} compact={true} prefix={"\u20AC"} />;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== props.i18n) {
    t16 = props.i18n._("to");
    $[24] = props.i18n;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t16) {
    t17 = <span className="px-2">{t16}</span>;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== props.i18n) {
    t18 = props.i18n._("Max. Budget");
    $[28] = props.i18n;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const t19 = maxBudget.value || undefined;
  let t20;
  if ($[30] !== maxBudget) {
    t20 = newMax => maxBudget.setValue(newMax || null);
    $[30] = maxBudget;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== t18 || $[33] !== t19 || $[34] !== t20) {
    t21 = <NumberInput title={t18} placeholder="0" className="mb-0!" min={0} value={t19} onChange={t20} step={1} hideButtons={true} compact={true} prefix={"\u20AC"} data-budget-max="" />;
    $[32] = t18;
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t15 || $[37] !== t17 || $[38] !== t21) {
    t22 = <div className="flex items-center gap-2 text-sm text-blue">{t15}{t17}{t21}</div>;
    $[36] = t15;
    $[37] = t17;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== t11 || $[41] !== t22 || $[42] !== t9) {
    t23 = <div className={t9}>{t11}{t22}</div>;
    $[40] = t11;
    $[41] = t22;
    $[42] = t9;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== isBizz || $[45] !== props.i18n || $[46] !== vatPref.enabled || $[47] !== vatPref.setEnabled) {
    t24 = !isBizz && <div><FilterDrawerFieldLabel>{props.i18n._("VAT")}</FilterDrawerFieldLabel><div className="flex items-center justify-between"><label htmlFor="vat-toggle" className="cursor-pointer text-sm text-blue">{props.i18n._("Show prices VAT incl.")}</label><Switch id="vat-toggle" data-testid="vat-toggle" size="sm" checked={vatPref.enabled} onCheckedChange={vatPref.setEnabled} /></div></div>;
    $[44] = isBizz;
    $[45] = props.i18n;
    $[46] = vatPref.enabled;
    $[47] = vatPref.setEnabled;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== badge || $[50] !== props.defaultClosed || $[51] !== props.i18n || $[52] !== t1 || $[53] !== t2 || $[54] !== t23 || $[55] !== t24 || $[56] !== t5 || $[57] !== t6) {
    t25 = <FilterLayout id="budget" i18n={t0} icon="budget" title={t1} focusContentOnOpen={t2} focusSelector="input[data-budget-max]" defaultClosed={t3} searchKeywords={t4} badge={badge} activeCount={t5} onClear={t6}>{t23}{t24}</FilterLayout>;
    $[49] = badge;
    $[50] = props.defaultClosed;
    $[51] = props.i18n;
    $[52] = t1;
    $[53] = t2;
    $[54] = t23;
    $[55] = t24;
    $[56] = t5;
    $[57] = t6;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  return t25;
};
const FilterSeason: React.FC<FilterProps & {
  defaultClosed?: boolean;
}> = props => {
  const state = props.state.getFilter('season');
  const OPTIONS = useMemo(() => [{
    value: 'SPRING',
    label: props.i18n._('Spring'),
    icon: 'spring'
  }, {
    value: 'SUMMER',
    label: props.i18n._('Summer'),
    icon: 'summer'
  }, {
    value: 'FALL',
    label: props.i18n._('Fall'),
    icon: 'autumn'
  }, {
    value: 'WINTER',
    label: props.i18n._('Winter'),
    icon: 'winter'
  }] as const satisfies Array<{
    value: string; // TODO: strict types
    label: React.ReactNode;
    icon: IconName;
  }>, [props.i18n.locale]);
  return <FilterLayout i18n={props.i18n} id="season" icon="calendar" title={props.i18n._('Season')} searchKeywords={OPTIONS.map(o => String(o.label))} defaultClosed={props.defaultClosed} badge={state.value?.length ? OPTIONS.filter(o_0 => state.value.includes(o_0.value)).map(o_1 => o_1.label).join(', ') : undefined} activeCount={state.value?.length ?? 0} onClear={!!state.value?.length && (() => state.setValue([]))}>
            <FilterTileGroup columns={2}>
                {OPTIONS.map((option, i) => {
        const selected = state.value.includes(option.value);
        return <FilterTileButton key={option.value} selected={selected} horizontal index={i} total={OPTIONS.length} columns={2} onClick={() => state.setValue(prev => std.Array.toggle(prev ?? [], option.value))}>
                            {!!option.icon && <Icon name={option.icon} className={`size-3.5 shrink-0 ${selected ? 'text-secondary' : 'text-blue-600'}`} size="sm" strokeWidth={2} />}
                            <span className="text-xs leading-tight">{option.label}</span>
                        </FilterTileButton>;
      })}
            </FilterTileGroup>
        </FilterLayout>;
};
const FilterLanguages: React.FC<FilterProps & {
  defaultClosed?: boolean;
}> = props => {
  const $ = _c(14);
  const query = useLanguagesQuery();
  const languageCounts = useActivityFilterDrawerStore(_temp4);
  let t0;
  if ($[0] !== query.languages) {
    t0 = query.languages ?? [];
    $[0] = query.languages;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = t0.map(_temp5);
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const options = t1;
  let t2;
  if ($[4] !== options) {
    t2 = options.map(_temp6);
    $[4] = options;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const searchKeywords = t2;
  let t3;
  if ($[6] !== props.i18n) {
    t3 = props.i18n._("Language");
    $[6] = props.i18n;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== languageCounts || $[9] !== options || $[10] !== props || $[11] !== searchKeywords || $[12] !== t3) {
    t4 = <MultiselectFilter {...props} id="language" icon="language" title={t3} filter="languages" defaultClosed={props.defaultClosed} options={options} counts={languageCounts} searchKeywords={searchKeywords} />;
    $[8] = languageCounts;
    $[9] = options;
    $[10] = props;
    $[11] = searchKeywords;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};

/**
 * Hands the text in the search box to the AI chat.
 *
 * `startPrompt` is the carrier `ActivityAiAutoPrompt` reads, the same one the
 * homepage uses. The sidebar and the BIZZ rail both offer this, so the wiring
 * lives here once rather than twice.
 */
function useAskAi(queryFilter) {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsString.withOptions(ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [, setStartPrompt] = useQueryState("startPrompt", t0);
  let t1;
  if ($[1] !== queryFilter || $[2] !== setStartPrompt) {
    t1 = () => {
      const question = (queryFilter.value ?? "").trim();
      if (question.length === 0) {
        return;
      }
      queryFilter.setValue(null);
      setStartPrompt(question);
    };
    $[1] = queryFilter;
    $[2] = setStartPrompt;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const ask = t1;
  let t2;
  if ($[4] !== ask) {
    t2 = (event, enabled) => {
      if (!enabled || event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) {
        return;
      }
      event.preventDefault();
      ask();
    };
    $[4] = ask;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== ask || $[7] !== t2) {
    t3 = {
      ask,
      onKeyDown: t2
    };
    $[6] = ask;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/** Zero results and not chosen: offering it only moves the visitor from one
 * empty page to another. An option that IS chosen stays clickable whatever its
 * count, because deselecting it is the only way out of an empty result. */
function isFacetOptionOffered(counts: Record<string, number> | undefined, key: string | number, isChecked: boolean): boolean {
  if (isChecked) return true;
  // Before the counts land nothing is withdrawn. Once they are here an option
  // the server did not mention has no results, and the label already shows it
  // as 0, so it is withdrawn like an explicit zero.
  if (!counts) return true;
  return (counts[key] ?? 0) !== 0;
}
const MultiselectFilter = props => {
  const $ = _c(37);
  let T0;
  let T1;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== props.counts || $[1] !== props.defaultClosed || $[2] !== props.filter || $[3] !== props.i18n || $[4] !== props.icon || $[5] !== props.id || $[6] !== props.options || $[7] !== props.searchKeywords || $[8] !== props.state || $[9] !== props.title) {
    const state = props.state.getFilter(props.filter);
    T1 = FilterLayout;
    t1 = props.id ?? props.filter;
    t2 = props.i18n;
    t3 = props.title;
    t4 = props.icon;
    t5 = props.defaultClosed;
    t6 = props.searchKeywords;
    t7 = state.value?.length ? props.options.filter(o => (state.value as unknown[]).includes(o.value)).map(_temp7).join(", ") : undefined;
    t8 = state.value?.length ?? 0;
    t9 = !!state.value?.length && (() => state.setValue([]));
    T0 = FilterDrawerCheckboxList;
    t0 = props.options.map(option => {
      const key = `${props.filter}-${option.value}`;
      const selectedValues = (state.value ?? []) as FilterOptionValue[];
      const isChecked = selectedValues.includes(option.value);
      const isOffered = isFacetOptionOffered(props.counts, String(option.value), isChecked);
      return <FilterCheckboxLabel htmlFor={key} key={key} disabled={!isOffered}><Checkbox id={key} disabled={!isOffered} checked={isChecked} onChange={() => {
          const nextValue = std.Array.toggle(selectedValues, option.value) as useActivitySearchCtx.FilterValues[N];
          state.setValue(nextValue);
        }} /><span className="flex-1 text-sm">{option.label}</span>{props.counts && <span className="ml-auto shrink-0 text-xs text-blue-400">{props.counts[String(option.value)] ?? 0}</span>}</FilterCheckboxLabel>;
    });
    $[0] = props.counts;
    $[1] = props.defaultClosed;
    $[2] = props.filter;
    $[3] = props.i18n;
    $[4] = props.icon;
    $[5] = props.id;
    $[6] = props.options;
    $[7] = props.searchKeywords;
    $[8] = props.state;
    $[9] = props.title;
    $[10] = T0;
    $[11] = T1;
    $[12] = t0;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[10];
    T1 = $[11];
    t0 = $[12];
    t1 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t10;
  if ($[22] !== T0 || $[23] !== t0) {
    t10 = <T0>{t0}</T0>;
    $[22] = T0;
    $[23] = t0;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== T1 || $[26] !== t1 || $[27] !== t10 || $[28] !== t2 || $[29] !== t3 || $[30] !== t4 || $[31] !== t5 || $[32] !== t6 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t11 = <T1 id={t1} i18n={t2} title={t3} icon={t4} defaultClosed={t5} searchKeywords={t6} badge={t7} activeCount={t8} onClear={t9}>{t10}</T1>;
    $[25] = T1;
    $[26] = t1;
    $[27] = t10;
    $[28] = t2;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
};

// We seriously need to find a way to have numbers in our value & options ...
const FilterDuration: React.FC<FilterProps & {
  defaultClosed?: boolean;
}> = props => {
  const minDuration = props.state.getFilter('minDuration');
  const maxDuration = props.state.getFilter('maxDuration');
  const OPTIONS = useMemo(() => [{
    value: 15,
    label: props.i18n._('15 minutes')
  }, {
    value: 30,
    label: props.i18n._('Half an hour')
  }, {
    value: 60,
    label: props.i18n._('1 hour')
  }, {
    value: 120,
    label: props.i18n._('2 hours')
  }, {
    value: 240,
    label: props.i18n._('Half a day')
  }, {
    value: 480,
    label: props.i18n._('Full day')
  }], [props.i18n.locale]);
  return <FilterLayout id="duration" i18n={props.i18n} icon="time" title={props.i18n._('Duration')} searchKeywords={OPTIONS.map(o => String(o.label))} defaultClosed={props.defaultClosed} badge={minDuration.value !== null || maxDuration.value !== null ? `${OPTIONS.find(o_0 => o_0.value === minDuration.value)?.label ?? props.i18n._('No min.')} – ${OPTIONS.find(o_1 => o_1.value === maxDuration.value)?.label ?? props.i18n._('No max.')}` : undefined} activeCount={[minDuration.value, maxDuration.value].filter(v => v !== null).length} onClear={!!(minDuration.value !== null || maxDuration.value !== null) && (() => {
    props.state.update({
      minDuration: null,
      maxDuration: null
    });
  })}>
            <div className="flex gap-2">
                <div className="w-1/2 min-w-0">
                    <span className="mb-1 block pl-1 text-xs font-medium text-secondary">
                        {props.i18n._('Min.')}
                    </span>
                    <InputSelect
        // Key is needed because input doesn't empty when the reset button is pressed
        key={`min-${minDuration.value?.toString()}`} className="w-full min-w-0" placeholder={props.i18n._('No min.')} value={minDuration.value?.toString() || null} onChange={newMin => minDuration.setValue(Number(newMin))} options={OPTIONS.map(optionValueToString)} compact />
                </div>
                <div className="w-1/2 min-w-0">
                    <span className="mb-1 block pl-1 text-xs font-medium text-secondary">
                        {props.i18n._('Max.')}
                    </span>
                    <InputSelect
        // Key is needed because input doesn't empty when the reset button is pressed
        key={`max-${maxDuration.value?.toString()}`} className="w-full min-w-0" placeholder={props.i18n._('No max.')} value={maxDuration.value?.toString() || null} onChange={newMax => maxDuration.setValue(Number(newMax))} options={(minDuration.value === null ? OPTIONS : OPTIONS.filter(o_2 => o_2.value >= (minDuration.value ?? -1))).map(optionValueToString)} compact />
                </div>
            </div>
        </FilterLayout>;
};
const FilterTraitsAndCleanliness: React.FC<FilterProps & {
  defaultClosed?: boolean;
}> = props => {
  const cleanlinessState = props.state.getFilter('cleanliness');
  const CLEANLINESS_OPTIONS = useMemo(() => [{
    value: 'CLEAN',
    label: props.i18n._('Clean'),
    icon: 'cleanliness-clean'
  }, {
    value: 'WET',
    label: props.i18n._('Wet'),
    icon: 'cleanliness-wet'
  }, {
    value: 'DIRTY',
    label: props.i18n._('Dirty'),
    icon: 'cleanliness-dirty'
  }] as const satisfies Array<{
    value: string; // TODO: strict type
    label: React.ReactNode;
    icon: IconName;
  }>, [props.i18n.locale]);
  const TRAIT_FILTERS = useMemo(() => [{
    name: 'teamwork',
    label: props.i18n._('Teamwork')
  }, {
    name: 'action',
    label: props.i18n._('Action')
  }, {
    name: 'competition',
    label: props.i18n._('Competition')
  }, {
    name: 'learning',
    label: props.i18n._('Learning')
  }] as const satisfies Array<{
    name: keyof useActivitySearchCtx.FilterValues;
    label: React.ReactNode;
  }>, [props.i18n.locale]);
  const hasTraits = TRAIT_FILTERS.some(x => !!(props.state.value[x.name] as unknown[])?.length);
  const hasCleanliness = !!cleanlinessState.value?.length;
  const badge = [...(hasTraits ? TRAIT_FILTERS.filter(x_0 => !!(props.state.value[x_0.name] as unknown[])?.length).map(x_1 => x_1.label) : []), ...(hasCleanliness ? CLEANLINESS_OPTIONS.filter(o => cleanlinessState.value.includes(o.value)).map(o_0 => o_0.label) : [])].join(', ');
  return <FilterLayout id="traits" i18n={props.i18n} icon="star" title={props.i18n._('Traits')} searchKeywords={[...TRAIT_FILTERS.map(f => String(f.label)), ...CLEANLINESS_OPTIONS.map(o_1 => String(o_1.label))]} defaultClosed={props.defaultClosed} badge={badge || undefined} activeCount={TRAIT_FILTERS.reduce((sum, x_2) => sum + ((props.state.value[x_2.name] as unknown[])?.length ?? 0), 0) + (cleanlinessState.value?.length ?? 0)} onClear={(hasTraits || hasCleanliness) && (() => {
    props.state.update(std.Object.fromEntries(TRAIT_FILTERS.map(filter => [filter.name, null] as const)));
    cleanlinessState.setValue([]);
  })}>
            {/* Traits */}
            <div className="mb-3">
                <FilterDrawerFieldLabel>{props.i18n._('Traits')}</FilterDrawerFieldLabel>
                <div className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-0.5">
                    {TRAIT_FILTERS.map(filter_0 => {
          const state = props.state.getFilter(filter_0.name);
          const selectedValues = state.value ?? [];
          const key = `trait-${filter_0.name}`;
          return <Fragment key={key}>
                                <span className="truncate text-sm font-medium text-blue">
                                    {filter_0.label}
                                </span>
                                <div className="flex gap-2">
                                    {[1, 2, 3].map(value => <button key={`${key}-${value}`} type="button" className={cx('flex h-6 w-6 items-center justify-center rounded-full border text-xs transition', selectedValues.includes(value) ? 'border-secondary bg-secondary text-white' : 'border-blue-300 text-blue-500 hover:border-secondary hover:bg-secondary-50')} onClick={() => state.setValue(prev => std.Array.toggle(prev ?? [], value))}>
                                            {value}
                                        </button>)}
                                </div>
                            </Fragment>;
        })}
                </div>
                <p className="text-[11px] text-blue-400">
                    1 = {props.i18n._('Not important')}, 2 = {props.i18n._('Important')}, 3 ={' '}
                    {props.i18n._('Very important')}
                </p>
            </div>

            {/* Cleanliness */}
            <div>
                <FilterDrawerFieldLabel>{props.i18n._('Cleanliness')}</FilterDrawerFieldLabel>
                <FilterTileGroup columns={3}>
                    {CLEANLINESS_OPTIONS.map((option, i) => {
          const selected = cleanlinessState.value.includes(option.value);
          return <FilterTileButton key={option.value} selected={selected} horizontal index={i} total={CLEANLINESS_OPTIONS.length} columns={3} onClick={() => cleanlinessState.setValue(prev_0 => std.Array.toggle(prev_0 ?? [], option.value))}>
                                {option.icon && <Icon name={option.icon} className={`size-3.5 shrink-0 ${selected ? 'text-secondary' : 'text-blue-600'}`} size="sm" viewBox="0 0 48 48" strokeWidth={2} />}
                                <span className="text-xs leading-tight">{option.label}</span>
                            </FilterTileButton>;
        })}
                </FilterTileGroup>
            </div>
        </FilterLayout>;
};
const FilterLocationCombined: React.FC<FilterProps & {
  defaultClosed?: boolean;
}> = props => {
  const locationState = props.state.getFilter('location');

  // City + radius distance search (same controls as the top "Locatie" dropdown).
  const posthog = usePostHog();
  const {
    regions,
    location,
    nearCleared
  } = props.state.value;
  const nearLabel = nearCleared ? null : props.state.value.nearLabel;
  const radius = nearCleared ? null : props.state.value.radius;
  // Gated on `nearCleared` like the label and radius above: a cleared point must not be
  // written back to the shared last-location storage the PDP widget reads.
  const nearLat = nearCleared ? null : props.state.value.nearLat;
  const nearLng = nearCleared ? null : props.state.value.nearLng;
  const hasNearPoint = isNearPointActive(props.state.value);
  const hasPrefilledNearPoint = useHasPrefilledNearPoint();

  // Share the last-searched location / delivery mode so the PDP transport widget + checkout
  // can reuse them. Written from the change handlers, not from effects: as effects they
  // re-ran on every mount and clobbered a newer value the PDP widget had just written.
  const rememberLocation = (next: {
    lat: number | null;
    lng: number | null;
    label: string | null;
    radius: number | null;
  }) => {
    if (next.lat == null || next.lng == null || typeof window === 'undefined') return;
    LAST_LOCATION_STORAGE.setValue({
      query: next.label ?? '',
      lat: next.lat,
      lng: next.lng,
      radius: next.radius
    });
  };
  const rememberMode = (routes: ReadonlyArray<ActivityLocationFilterType>) => {
    if (typeof window === 'undefined') return;
    LAST_LOCATION_MODE.setValue(routes.length === 0 ? null : routes.includes('CUSTOM') && !routes.includes('FIXED') ? 'custom' : 'fixed');
  };
  const [radiusSearch, setRadiusSearch] = useState('');
  const radiusOptions = useMemo(() => {
    const kms = new Set<number>(RADIUS_OPTIONS);
    if (radius != null) kms.add(radius);
    const typed = Number(radiusSearch.trim());
    if (Number.isFinite(typed) && typed > 0) kms.add(typed);
    return [...kms].sort((a, b) => a - b).map(km => ({
      value: String(km),
      label: `${km} km`
    }));
  }, [radiusSearch, radius]);
  const ROUTE_OPTIONS = useMemo(() => [{
    value: 'FIXED' as ActivityLocationFilterType,
    label: props.i18n._('activity.filter.location.option.fixed', undefined, {
      message: 'I go to the partner'
    }),
    icon: 'car-retro' as IconName
  }, {
    value: 'CUSTOM' as ActivityLocationFilterType,
    label: props.i18n._('activity.filter.location.option.custom', undefined, {
      message: 'Location of choice'
    }),
    icon: 'house-pin' as IconName
  }], [props.i18n.locale]);
  const hasLocation = !!locationState.value?.length;
  const canClearNearPoint = !!nearLabel || hasNearPoint;
  const clearNearPoint = (extra?: Partial<Parameters<typeof props.state.update>[0]>) => props.state.update(NEAR_POINT_CLEARED({
    hasPrefilledNearPoint,
    extra
  }));

  // Multi-select: each route toggles independently — both can be enabled at once.
  const isRoutePressed = (type: ActivityLocationFilterType) => (location ?? []).includes(type);
  const toggleRoute = (type_0: ActivityLocationFilterType) => {
    const next_0 = std.Array.toggle(location ?? [], type_0) as useActivitySearchCtx.FilterValues['location'];
    locationState.setValue(next_0);
    rememberMode(next_0 ?? []);
  };
  const badge = [...(hasLocation ? ROUTE_OPTIONS.filter(o => (locationState.value as string[]).includes(o.value)).map(o_0 => o_0.label) : []), ...(nearLabel ? [nearLabel] : [])].join(', ');
  return <FilterLayout i18n={props.i18n} id="location" icon="home-2" defaultClosed={props.defaultClosed} title={props.i18n._('activity.filter.location.title', undefined, {
    message: 'Location'
  })} searchKeywords={[...ROUTE_OPTIONS.map(o_1 => String(o_1.label)), String(props.i18n._('Search by city'))]} badge={badge || undefined} activeCount={(location?.length ?? 0) + (nearLabel ? 1 : 0) + (regions?.length ?? 0)} onClear={
  // `nearLabel` counts on its own: a label can outlive its
  // coordinates, and without it in this condition the section
  // reset disappears while the search is still constrained.
  (hasLocation || hasNearPoint || !!nearLabel || !!regions?.length) && (() => {
    clearNearPoint({
      location: [],
      regions: [],
      sort: null
    });
    rememberMode([]);
  })}>
            {/* City + distance — joined card */}
            <div className="mb-3 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-gray-200 bg-gray-200">
                {/* `ComboBox` can only ever *select* an option — it never emits an
                 * empty value — so without this overlay a picked city is
                 * permanent and the catalog stays pinned to it (FUN-4743). */}
                <div className="relative">
                    {canClearNearPoint ? <button type="button" onClick={() => clearNearPoint()} data-testid="filter-location-clear" aria-label={props.i18n._('activity.filter.location.clear', undefined, {
          message: 'Clear location'
        })} title={props.i18n._('activity.filter.location.clear', undefined, {
          message: 'Clear location'
        })} className="absolute right-3 top-1/2 z-1 -translate-y-1/2 rounded-full p-1 text-blue-400 transition hover:bg-blue-50 hover:text-primary">
                            <Icon name="cross" size="xs" />
                        </button> : null}
                    <GoogleMapsAutocomplete value={nearLabel ?? ''} placeholder={props.i18n._('Enter a city or address')} searchPlaceHolder={props.i18n._('google-maps.search.placeholder')}
        // Only reserve room for the clear button while it is
        // actually there — otherwise the empty field's chevron
        // sits inset and breaks alignment with the radius row.
        className={cx('rounded-none border-0 shadow-none', canClearNearPoint && 'pr-10')} onChange={label => {
          // An emptied label must drop the coordinates too, or
          // the point filter keeps applying invisibly.
          if (!label) {
            clearNearPoint();
            return;
          }
          props.state.update({
            nearLabel: label
          });
          rememberLocation({
            lat: nearLat,
            lng: nearLng,
            label,
            radius: radius ?? null
          });
        }} onResolve={coords => {
          posthog.capture('location.search', {
            near_label: nearLabel ?? null,
            latitude: Number(coords.latitude),
            longitude: Number(coords.longitude),
            radius_km: coords.suggestedRadiusKm,
            routes: location,
            source: 'sidebar'
          });
          props.state.update(NEAR_POINT_PICKED({
            latitude: coords.latitude,
            longitude: coords.longitude,
            radiusKm: coords.suggestedRadiusKm
          }));
          rememberLocation({
            lat: Number(coords.latitude),
            lng: Number(coords.longitude),
            label: nearLabel ?? null,
            radius: coords.suggestedRadiusKm
          });
        }} />
                </div>
                <ComboBox value={radius != null ? `${radius} km` : ''} options={radiusOptions} placeholder={props.i18n._('Any distance')} searchPlaceHolder={props.i18n._('e.g. 30')} onSearch={val => {
        setRadiusSearch(val);
        // Apply a typed custom value live — no need to reselect it.
        const typed_0 = Number.parseInt(val, 10);
        if (Number.isFinite(typed_0) && typed_0 > 0) {
          props.state.update({
            radius: typed_0
          });
          rememberLocation({
            lat: nearLat,
            lng: nearLng,
            label: nearLabel ?? null,
            radius: typed_0
          });
        }
      }} onChange={selected => {
        props.state.update({
          radius: selected ? Number.parseInt(selected, 10) : null
        });
        rememberLocation({
          lat: nearLat,
          lng: nearLng,
          label: nearLabel ?? null,
          radius: selected ? Number.parseInt(selected, 10) : null
        });
      }} className="w-full rounded-none border-0 shadow-none" />
            </div>

            {/* Routes (voorkeurslocatie) — joined card, stacked vertically. */}
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-gray-200 bg-gray-200">
                {ROUTE_OPTIONS.map(option => <Toggle key={option.value} data-testid={`location-route-${option.value}`} className="group h-12 justify-start gap-3 whitespace-normal rounded-none border-0 bg-white px-4 text-left leading-tight hover:bg-white data-[state=on]:hover:bg-secondary-50" pressed={isRoutePressed(option.value)} onPressedChange={() => toggleRoute(option.value)}>
                        <Checkbox checked={isRoutePressed(option.value)} readOnly tabIndex={-1} className="pointer-events-none shrink-0" />
                        <span className="min-w-0 flex-1">{option.label}</span>
                        <Icon name={option.icon} strokeWidth={1.5} className="size-4.5! shrink-0 text-primary group-data-[state=on]:text-secondary" />
                    </Toggle>)}
            </div>
        </FilterLayout>;
};

/** Location type (indoor / outdoor / online) — its own section, split out of Locatie. */
const FilterLocationType: React.FC<FilterProps & {
  defaultClosed?: boolean;
}> = props => {
  const locationTypeState = props.state.getFilter('locationType');
  const locationTypeCounts = useActivityFilterDrawerStore(s => s.facetCounts?.locationType);
  const LOCATION_TYPE_OPTIONS = useMemo(() => [{
    value: 'INDOOR',
    label: props.i18n._('Indoor'),
    icon: 'location-indoor'
  }, {
    value: 'OUTDOOR',
    label: props.i18n._('Outdoor'),
    icon: 'location-outdoor'
  }, {
    value: 'VIRTUAL',
    label: props.i18n._('Online / Virtual'),
    icon: 'location-online'
  }] as const satisfies Array<{
    value: NonNullable<typeof locationTypeState.value>[number];
    label: React.ReactNode;
    icon: IconName;
  }>, [props.i18n.locale]);
  const hasLocationType = !!locationTypeState.value?.length;
  return <FilterLayout i18n={props.i18n} id="locationType" icon="activity-selection" defaultClosed={props.defaultClosed} title={props.i18n._('activity.filter.location_type.title', undefined, {
    message: 'Location type'
  })} searchKeywords={LOCATION_TYPE_OPTIONS.map(o => String(o.label))} badge={hasLocationType ? LOCATION_TYPE_OPTIONS.filter(o_0 => locationTypeState.value.includes(o_0.value)).map(o_1 => o_1.label).join(', ') : undefined} activeCount={locationTypeState.value?.length ?? 0} onClear={hasLocationType && (() => locationTypeState.setValue([]))}>
            <FilterDrawerCheckboxList>
                {LOCATION_TYPE_OPTIONS.map(option => {
        const selected = locationTypeState.value.includes(option.value);
        const key = `locationType-${option.value}`;
        const isOffered = isFacetOptionOffered(locationTypeCounts, option.value, selected);
        return <FilterCheckboxLabel key={key} htmlFor={key} disabled={!isOffered}>
                            <Checkbox id={key} disabled={!isOffered} checked={selected} onChange={() => locationTypeState.setValue(prev => std.Array.toggle(prev ?? [], option.value))} />
                            {!!option.icon && <Icon name={option.icon} className={`size-4 shrink-0 ${selected ? 'text-secondary' : 'text-blue-600'}`} size="sm" viewBox="0 0 48 48" />}
                            <span className="flex-1 text-sm">{option.label}</span>
                            {locationTypeCounts && <span data-testid="filter-location-type-count" className="ml-auto shrink-0 text-xs text-blue-400">
                                    {locationTypeCounts[option.value] ?? 0}
                                </span>}
                        </FilterCheckboxLabel>;
      })}
            </FilterDrawerCheckboxList>
        </FilterLayout>;
};
const FilterCategories: React.FC<FilterProps & {
  defaultClosed?: boolean;
}> = props => {
  const $ = _c(69);
  const portal = usePortalCtx();
  const searchCtx = useActivitySearchCtx();
  const tenantId = portal.portal === "BIZZ" ? portal.bizz.tenantId : undefined;
  const allowedCategories = searchCtx?.customization?.constraints?.allowedCategories;
  let t0;
  if ($[0] !== allowedCategories || $[1] !== tenantId) {
    t0 = {
      tenantId,
      allowedCategories
    };
    $[0] = allowedCategories;
    $[1] = tenantId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const query = useBrowseCategories(t0);
  let T0;
  let categoriesState;
  let customCategories;
  let globalCategories;
  let isBizzContext;
  let subCategoriesState;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] !== portal.portal || $[4] !== props.defaultClosed || $[5] !== props.i18n || $[6] !== props.state || $[7] !== query) {
    categoriesState = props.state.getFilter("categories");
    let t8;
    if ($[21] !== props.state) {
      t8 = props.state.getFilter("subCategories");
      $[21] = props.state;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    subCategoriesState = t8;
    isBizzContext = portal.portal === "BIZZ";
    let t9;
    if ($[23] !== query.categories) {
      t9 = query.categories ?? [];
      $[23] = query.categories;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    const all = t9;
    let t10;
    if ($[25] !== all) {
      t10 = all.filter(_temp24);
      $[25] = all;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    const custom = t10;
    let t11;
    if ($[27] !== all) {
      t11 = all.filter(_temp25);
      $[27] = all;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    const global = t11;
    let t12;
    if ($[29] !== custom || $[30] !== global) {
      t12 = {
        customCategories: custom,
        globalCategories: global
      };
      $[29] = custom;
      $[30] = global;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    ({
      customCategories,
      globalCategories
    } = t12);
    let t13;
    if ($[32] !== query.categories) {
      t13 = query.categories?.flatMap(_temp27) ?? [];
      $[32] = query.categories;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    const searchKeywords = t13;
    T0 = FilterLayout;
    t1 = "categories";
    t2 = props.i18n;
    t3 = "collection";
    if ($[34] !== props.i18n) {
      t4 = props.i18n._("Categories");
      $[34] = props.i18n;
      $[35] = t4;
    } else {
      t4 = $[35];
    }
    t5 = searchKeywords;
    t6 = props.defaultClosed;
    t7 = categoriesState.value.length ? query.categories?.filter(c_2 => categoriesState.value.includes(Number(c_2.id))).map(_temp28).join(", ") : undefined;
    $[3] = portal.portal;
    $[4] = props.defaultClosed;
    $[5] = props.i18n;
    $[6] = props.state;
    $[7] = query;
    $[8] = T0;
    $[9] = categoriesState;
    $[10] = customCategories;
    $[11] = globalCategories;
    $[12] = isBizzContext;
    $[13] = subCategoriesState;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    T0 = $[8];
    categoriesState = $[9];
    customCategories = $[10];
    globalCategories = $[11];
    isBizzContext = $[12];
    subCategoriesState = $[13];
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
  }
  const t8 = categoriesState.value.length + subCategoriesState.value.length;
  let t9;
  if ($[36] !== categoriesState.value.length || $[37] !== props.state || $[38] !== subCategoriesState.value.length) {
    t9 = !!(categoriesState.value.length || subCategoriesState.value.length) && (() => props.state.update({
      categories: [],
      subCategories: []
    }));
    $[36] = categoriesState.value.length;
    $[37] = props.state;
    $[38] = subCategoriesState.value.length;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== categoriesState || $[41] !== customCategories || $[42] !== globalCategories || $[43] !== isBizzContext || $[44] !== props.i18n || $[45] !== subCategoriesState) {
    t10 = isBizzContext && customCategories.length > 0 && <><div className="mb-1 text-xs font-medium text-secondary">{props.i18n._("filters.categories.custom")}</div><FilterCategoriesList categories={customCategories} categoriesState={categoriesState} subCategoriesState={subCategoriesState} />{globalCategories.length > 0 && <div className="mb-1 mt-3 text-xs font-medium text-secondary">{props.i18n._("filters.categories.general")}</div>}</>;
    $[40] = categoriesState;
    $[41] = customCategories;
    $[42] = globalCategories;
    $[43] = isBizzContext;
    $[44] = props.i18n;
    $[45] = subCategoriesState;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== customCategories || $[48] !== globalCategories || $[49] !== isBizzContext || $[50] !== query.categories) {
    t11 = isBizzContext && customCategories.length > 0 ? globalCategories : query.categories?.filter(_temp29) ?? [];
    $[47] = customCategories;
    $[48] = globalCategories;
    $[49] = isBizzContext;
    $[50] = query.categories;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] !== categoriesState || $[53] !== subCategoriesState || $[54] !== t11) {
    t12 = <FilterCategoriesList categories={t11} categoriesState={categoriesState} subCategoriesState={subCategoriesState} />;
    $[52] = categoriesState;
    $[53] = subCategoriesState;
    $[54] = t11;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  let t13;
  if ($[56] !== T0 || $[57] !== t1 || $[58] !== t10 || $[59] !== t12 || $[60] !== t2 || $[61] !== t3 || $[62] !== t4 || $[63] !== t5 || $[64] !== t6 || $[65] !== t7 || $[66] !== t8 || $[67] !== t9) {
    t13 = <T0 id={t1} i18n={t2} icon={t3} title={t4} searchKeywords={t5} defaultClosed={t6} badge={t7} activeCount={t8} onClear={t9}>{t10}{t12}</T0>;
    $[56] = T0;
    $[57] = t1;
    $[58] = t10;
    $[59] = t12;
    $[60] = t2;
    $[61] = t3;
    $[62] = t4;
    $[63] = t5;
    $[64] = t6;
    $[65] = t7;
    $[66] = t8;
    $[67] = t9;
    $[68] = t13;
  } else {
    t13 = $[68];
  }
  return t13;
};
const FilterCategoriesList: React.FC<{
  categories: NonNullable<ReturnType<typeof useCategoriesQuery>['categories']>;
  categoriesState: {
    value: number[];
    setValue: React.Dispatch<React.SetStateAction<number[]>>;
  };
  subCategoriesState: {
    value: number[];
    setValue: React.Dispatch<React.SetStateAction<number[]>>;
  };
}> = t0 => {
  const $ = _c(11);
  const {
    categories,
    categoriesState,
    subCategoriesState
  } = t0;
  const categoryCounts = useActivityFilterDrawerStore(_temp30);
  const searchTerm = useContext(SidebarSearchCtx);
  const isSearching = searchTerm.length > 0;
  let t1;
  let t2;
  if ($[0] !== categories || $[1] !== categoriesState || $[2] !== categoryCounts || $[3] !== isSearching || $[4] !== searchTerm || $[5] !== subCategoriesState) {
    const q = searchTerm.toLowerCase();
    t1 = "flex flex-col gap-0.5";
    t2 = categories.map(option => {
      const ID = Number(option.id);
      const isChecked = (categoriesState.value as number[]).includes(ID);
      const key = `category-${ID}`;
      const hasChildMatch = isSearching && option?.children?.some(child => child?.name?.toLowerCase().includes(q));
      const parentMatches = isSearching && option.name?.toLowerCase().includes(q);
      const shouldExpandForSearch = isSearching && (hasChildMatch || parentMatches);
      const hasSubSelected = option?.children?.some(child_0 => child_0 && subCategoriesState.value.includes(Number(child_0.id)));
      const isActive = isChecked || hasSubSelected;
      return <div key={key} className={cx("flex w-full flex-col overflow-hidden rounded-xl px-2 py-1", isActive && "bg-brown-50/50")}><FilterCheckboxLabel htmlFor={key} disabled={!isFacetOptionOffered(categoryCounts, ID, isActive)}><Checkbox id={key} disabled={!isFacetOptionOffered(categoryCounts, ID, isActive)} checked={isChecked} onChange={_e => categoriesState.setValue(prev => std.Array.toggle(prev, ID))} /><span className="flex-1 text-sm">{option.name}</span>{(() => {
            const selectedSubCount = option.children?.filter(child_1 => child_1 && subCategoriesState.value.includes(Number(child_1.id))).length ?? 0;
            if (categoryCounts) {
              return <span data-testid="filter-category-count" className="ml-auto shrink-0 text-xs text-blue-400">{categoryCounts[ID] ?? 0}</span>;
            }
            return selectedSubCount > 0 ? <span className="ml-auto shrink-0 rounded-full bg-secondary-100 px-1.5 py-0.5 text-[11px] font-medium leading-normal text-secondary-700">{selectedSubCount}</span> : option.children?.length ? <span className="ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-medium leading-normal text-blue-400">{option.children.length}</span> : null;
          })()}</FilterCheckboxLabel><AnimatePresence initial={false}>{(isChecked || shouldExpandForSearch || option?.children?.some(child_2 => child_2 && (subCategoriesState.value as number[]).includes(Number(child_2.id)))) && !!option?.children?.length && <motion.div key={`category-subs-${ID}`} initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: "auto",
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.25,
            ease: "easeOut"
          }} className="overflow-hidden"><FilterDrawerCheckboxList className="ml-5 pb-0.5 pt-0.5">{option.children?.map(subOption => {
                if (!subOption?.id) {
                  return null;
                }
                const subId = Number(subOption.id);
                const subChecked = subCategoriesState.value.includes(subId);
                const subKey = `category-${subId}`;
                return <FilterCheckboxLabel htmlFor={subKey} key={subKey} disabled={!isFacetOptionOffered(categoryCounts, subId, subChecked)}><Checkbox id={subKey} disabled={!isFacetOptionOffered(categoryCounts, subId, subChecked)} checked={subChecked} onChange={_e_0 => subCategoriesState.setValue(prev_0 => std.Array.toggle(prev_0, subId))} /><span className="flex-1 text-sm">{subOption.name}</span>{categoryCounts && <span className="ml-auto shrink-0 text-xs text-blue-400">{categoryCounts[subId] ?? 0}</span>}</FilterCheckboxLabel>;
              })}</FilterDrawerCheckboxList></motion.div>}</AnimatePresence></div>;
    });
    $[0] = categories;
    $[1] = categoriesState;
    $[2] = categoryCounts;
    $[3] = isSearching;
    $[4] = searchTerm;
    $[5] = subCategoriesState;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
const FilterParticipants: React.FC<FilterProps & {
  defaultClosed?: boolean;
}> = props => {
  const $ = _c(22);
  let t0;
  if ($[0] !== props.state) {
    t0 = props.state.getFilter("participants");
    $[0] = props.state;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const state = t0;
  const t1 = props.i18n;
  let t2;
  if ($[2] !== props.i18n) {
    t2 = props.i18n._("Participant count");
    $[2] = props.i18n;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = props.defaultClosed;
  const t4 = state.value !== null ? `${state.value}` : undefined;
  const t5 = state.value !== null ? 1 : 0;
  let t6;
  if ($[4] !== state) {
    t6 = state.value !== null && (() => state.setValue(null));
    $[4] = state;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== props.i18n) {
    t7 = props.i18n._("Choose amount");
    $[6] = props.i18n;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== state) {
    t8 = newMin => state.setValue(newMin || null);
    $[8] = state;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = state.value || undefined;
  let t10;
  if ($[10] !== t7 || $[11] !== t8 || $[12] !== t9) {
    t10 = <div className="flex items-center gap-2 text-sm text-blue"><NumberInput id="people" name="people" data-testid="activity-plp-participants-input" allowUndefined={true} placeholder={t7} className="mb-0!" onChange={t8} min={0} max={100} value={t9} step={1} compact={true} /></div>;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== props.defaultClosed || $[15] !== props.i18n || $[16] !== t10 || $[17] !== t2 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t11 = <FilterLayout id="participants" i18n={t1} icon="people-users" title={t2} focusContentOnOpen={true} defaultClosed={t3} badge={t4} activeCount={t5} onClear={t6}>{t10}</FilterLayout>;
    $[14] = props.defaultClosed;
    $[15] = props.i18n;
    $[16] = t10;
    $[17] = t2;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
};

// ── Commented out: accessibility & doelgroep filters (re-enable after merge) ──
// type AccessibilityGroup = {
//     label: string;
//     options: Array<{ value: ActivityAccessibilityFeature; label: string }>;
// };
//
// const getAccessibilityGroups = ...
// const FilterAccessibilityAndAudience = ...
// (Full component commented out to avoid data conflicts with new tables)

const ActivityFiltersContent: React.FC<{
  filterProps: FilterProps;
  excludeTopBarFilters?: boolean;
  showBottomActions?: boolean;
}> = props => {
  const $ = _c(53);
  const ctx = useActivitySearchCtx();
  const posthog = usePostHog();
  let t0;
  if ($[0] !== posthog) {
    t0 = PostHog_ClientFeaturesSDK.getFlag(posthog, "plp-extra-filters", "control");
    $[0] = posthog;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const showExtraFilters = t0 === "enabled";
  const isFinetuning = usePlpExperiment();
  const hideTopBarFilters = isFinetuning && !!props.excludeTopBarFilters;
  const priceFiltersFirst = isFinetuning && !props.excludeTopBarFilters;
  let t1;
  if ($[2] !== ctx.hiddenFilters) {
    t1 = ctx.hiddenFilters.includes("budget");
    $[2] = ctx.hiddenFilters;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const showBudget = !t1;
  let t2;
  if ($[4] !== priceFiltersFirst || $[5] !== props.filterProps || $[6] !== showBudget) {
    t2 = priceFiltersFirst && <><FilterParticipants {...props.filterProps} defaultClosed={true} /><FilterLocationCombined {...props.filterProps} defaultClosed={true} />{showBudget && <FilterBudgetAndVAT {...props.filterProps} defaultClosed={true} />}</>;
    $[4] = priceFiltersFirst;
    $[5] = props.filterProps;
    $[6] = showBudget;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== hideTopBarFilters || $[9] !== isFinetuning) {
    t3 = !hideTopBarFilters && !isFinetuning && <FilterSortSection defaultClosed={true} />;
    $[8] = hideTopBarFilters;
    $[9] = isFinetuning;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== hideTopBarFilters || $[12] !== priceFiltersFirst || $[13] !== props.filterProps) {
    t4 = !hideTopBarFilters && !priceFiltersFirst && <FilterLocationCombined {...props.filterProps} defaultClosed={true} />;
    $[11] = hideTopBarFilters;
    $[12] = priceFiltersFirst;
    $[13] = props.filterProps;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== props.filterProps) {
    t5 = <FilterLanguages {...props.filterProps} defaultClosed={true} />;
    $[15] = props.filterProps;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== hideTopBarFilters || $[18] !== priceFiltersFirst || $[19] !== props.filterProps || $[20] !== showBudget) {
    t6 = showBudget && !priceFiltersFirst && <FilterBudgetAndVAT {...props.filterProps} defaultClosed={true} hideBudget={hideTopBarFilters} />;
    $[17] = hideTopBarFilters;
    $[18] = priceFiltersFirst;
    $[19] = props.filterProps;
    $[20] = showBudget;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== hideTopBarFilters || $[23] !== priceFiltersFirst || $[24] !== props.filterProps) {
    t7 = !hideTopBarFilters && !priceFiltersFirst && <FilterParticipants {...props.filterProps} defaultClosed={true} />;
    $[22] = hideTopBarFilters;
    $[23] = priceFiltersFirst;
    $[24] = props.filterProps;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[26] !== props.filterProps) {
    t8 = <FilterCategories {...props.filterProps} defaultClosed={true} />;
    t9 = <FilterLocationType {...props.filterProps} defaultClosed={true} />;
    t10 = <FilterDuration {...props.filterProps} defaultClosed={true} />;
    t11 = <FilterTraitsAndCleanliness {...props.filterProps} defaultClosed={true} />;
    $[26] = props.filterProps;
    $[27] = t10;
    $[28] = t11;
    $[29] = t8;
    $[30] = t9;
  } else {
    t10 = $[27];
    t11 = $[28];
    t8 = $[29];
    t9 = $[30];
  }
  let t12;
  if ($[31] !== props.filterProps || $[32] !== showExtraFilters) {
    t12 = showExtraFilters && <FilterSeason {...props.filterProps} defaultClosed={true} />;
    $[31] = props.filterProps;
    $[32] = showExtraFilters;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== props.filterProps || $[35] !== props.showBottomActions) {
    t13 = props.showBottomActions && <FilterBottomActions i18n={props.filterProps.i18n} state={props.filterProps.state} />;
    $[34] = props.filterProps;
    $[35] = props.showBottomActions;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== props.showBottomActions) {
    t14 = props.showBottomActions && <AiSearchSidebarBanner />;
    $[37] = props.showBottomActions;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t10 || $[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== t14 || $[44] !== t2 || $[45] !== t3 || $[46] !== t4 || $[47] !== t5 || $[48] !== t6 || $[49] !== t7 || $[50] !== t8 || $[51] !== t9) {
    t15 = <>{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}</>;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t2;
    $[45] = t3;
    $[46] = t4;
    $[47] = t5;
    $[48] = t6;
    $[49] = t7;
    $[50] = t8;
    $[51] = t9;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  return t15;
};

/**
 * The last thing under the filters: a way out for a visitor whose question the
 * filters cannot express ("iets actiefs in Gent voor 30 man onder de 50 euro").
 *
 * The search box at the top of the sidebar already hands its text to the AI
 * chat, but nothing says so, and a visitor who has worked down the filters is
 * exactly the one who needs to know.
 */
const AiSearchSidebarBanner: React.FC = () => {
  const $ = _c(17);
  const i18n = useI18n();
  const isFinetuning = usePlpExperiment();
  const {
    isAi,
    setMode
  } = useActivitySearchMode();
  if (!isFinetuning || isAi) {
    return null;
  }
  let t0;
  if ($[0] !== setMode) {
    t0 = () => void setMode("ai");
    $[0] = setMode;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary text-white"><Sparkles className="size-4" aria-hidden={true} /></span>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== i18n) {
    t2 = i18n._("activity.plp.aiSearch.banner.title", undefined, {
      message: "Search faster?"
    });
    $[3] = i18n;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <span className="text-sm font-bold leading-tight text-primary">{t2}</span>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== i18n) {
    t4 = i18n._("activity.plp.aiSearch.banner.description", undefined, {
      message: "Our AI chatbot gives you targeted recommendations!"
    });
    $[7] = i18n;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="text-sm leading-tight text-blue-500">{t4}</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <span className="flex min-w-0 flex-col gap-0.5">{t3}{t5}</span>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t0 || $[15] !== t6) {
    t7 = <button type="button" data-testid="sidebar-ai-search-banner" onClick={t0} className="mt-3 flex w-full items-start gap-3 rounded-2xl bg-secondary/10 px-3 py-3 text-left transition hover:bg-secondary/15">{t1}{t6}</button>;
    $[14] = t0;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
const FilterBottomActions: React.FC<{
  i18n: ReturnType<typeof useI18n>;
  state: useActivitySearchCtx.AllFiltersState;
  excludeTopBarFilters?: boolean;
}> = props => {
  const $ = _c(17);
  const ctx = useActivitySearchCtx();
  const {
    collapseAllSections,
    collapsed,
    opened
  } = useCollapsedFilters();
  let t0;
  if ($[0] !== props.state.value) {
    t0 = getActiveFilterCount(props.state.value);
    $[0] = props.state.value;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const activeFilterCount = t0;
  let t1;
  bb0: {
    for (const id of ACTIVITY_FILTER_SECTION_IDS) {
      if (ctx.hiddenFilters.includes("budget") && id === "budget") {
        continue;
      }
      const dc = (DEFAULT_CLOSED_SECTION_IDS as readonly string[]).includes(id);
      const isOpen = dc ? opened.includes(id) : !collapsed.includes(id);
      if (isOpen) {
        t1 = true;
        break bb0;
      }
    }
    t1 = false;
  }
  const hasAnyOpen = t1;
  let t2;
  if ($[2] !== collapseAllSections || $[3] !== hasAnyOpen || $[4] !== props.i18n) {
    t2 = hasAnyOpen && <button type="button" data-testid="sidebar-collapse-all-button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-blue-500 transition hover:bg-white hover:text-blue-700" onClick={collapseAllSections}><ChevronsDownUp className="size-3.5 shrink-0" aria-hidden={true} />{props.i18n._("activity.overview.filter.collapse_sections", undefined, {
        message: "Close all sections"
      })}</button>;
    $[2] = collapseAllSections;
    $[3] = hasAnyOpen;
    $[4] = props.i18n;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props.i18n) {
    t3 = <FilterCopyPageLinkButton i18n={props.i18n} />;
    $[6] = props.i18n;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== activeFilterCount || $[9] !== collapseAllSections || $[10] !== props.i18n || $[11] !== props.state) {
    t4 = !!activeFilterCount && <button type="button" data-testid="sidebar-bottom-reset-button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-tertiary transition hover:bg-white hover:text-tertiary-700" onClick={() => {
      collapseAllSections();
      props.state.clear();
    }}><Undo2 className="size-3.5 shrink-0" aria-hidden={true} />{props.i18n._("Delete all filters")}</button>;
    $[8] = activeFilterCount;
    $[9] = collapseAllSections;
    $[10] = props.i18n;
    $[11] = props.state;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = <div data-testid="sidebar-bottom-actions" className="mt-2 flex flex-col gap-1 pt-3">{t2}{t3}{t4}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
const optionValueToString = (option: {
  value: unknown;
  label: React.ReactNode;
}) => ({
  value: String(option.value),
  label: option.label
});
function _temp(prev_0) {
  const next_0 = [...new Set([...prev_0, ...ACTIVITY_FILTER_SECTION_IDS])];
  return next_0.length === 0 ? null : next_0;
}
function _temp4(s) {
  return s.facetCounts?.languages;
}
function _temp5(x) {
  return {
    value: Number(x.id),
    label: x.name
  };
}
function _temp6(o) {
  return String(o.label ?? "");
}
function _temp7(o_0) {
  return o_0.label;
}
function _temp24(c) {
  return c.isCustom;
}
function _temp25(c_0) {
  return c_0.isRoot && !c_0.isCustom;
}
function _temp26(s) {
  return s?.name ?? "";
}
function _temp27(c_1) {
  return [c_1.name ?? "", ...(c_1.children?.map(_temp26) ?? [])];
}
function _temp28(c_3) {
  return c_3.name;
}
function _temp29(c_4) {
  return c_4.isRoot;
}
function _temp30(s) {
  return s.facetCounts?.categories;
}